import React, { useEffect, useRef } from 'react';
import { gameStore, ZoneId } from './store';
import { getQuip, quips } from './quips';
import { storyBeats, mordecaiAmbient } from './dialogue';
import { ZONES, PropDef, PropType, CreatureType } from './world';
type Vec2 = {
  x: number;
  y: number;
};
const v2 = (x: number, y: number): Vec2 => ({
  x,
  y
});
const v2Add = (a: Vec2, b: Vec2): Vec2 => ({
  x: a.x + b.x,
  y: a.y + b.y
});
const v2Sub = (a: Vec2, b: Vec2): Vec2 => ({
  x: a.x - b.x,
  y: a.y - b.y
});
const v2Scale = (a: Vec2, s: number): Vec2 => ({
  x: a.x * s,
  y: a.y * s
});
const v2Len = (a: Vec2) => Math.sqrt(a.x * a.x + a.y * a.y);
const v2Norm = (a: Vec2) => {
  const l = v2Len(a);
  return l > 0 ? v2Scale(a, 1 / l) : v2(0, 0);
};
const v2Dist = (a: Vec2, b: Vec2) => v2Len(v2Sub(a, b));
type EntityKind = 'player' | 'enemy' | 'crate' | 'sponsor' | 'pillar' | 'npc' | 'portal' | 'boss' | 'stairs' | 'prop' | 'creature' | 'pickup';
type Entity = {
  id: string;
  pos: Vec2;
  vel: Vec2;
  size: number;
  mass: number;
  color: string;
  kind: EntityKind;
  hp: number;
  maxHp: number;
  isBrute: boolean;
  isAssassin: boolean;
  speed: number;
  lastDamageToPlayer: number;
  dead: boolean;
  friction: number;
  npcSpeaker?: string;
  npcLabel?: string;
  portalTarget?: ZoneId;
  portalLabel?: string;
  glowPhase?: number;
  // Prop metadata
  propType?: PropType;
  breakable?: boolean;
  dropsGold?: number;
  dropsHp?: number;
  propVariant?: number;
  // Creature metadata
  creatureType?: CreatureType;
  wanderTarget?: Vec2;
  wanderTimer?: number;
  // Pickup
  pickupValue?: number;
  pickupType?: 'gold' | 'health';
  // Player
  facing?: Vec2;
  damageFlash?: number;
  walkPhase?: number;
};
type Particle = {
  pos: Vec2;
  vel: Vec2;
  life: number;
  maxLife: number;
  color: string;
  size: number;
  kind: 'dust' | 'blood' | 'spark' | 'mote' | 'ring' | 'smoke' | 'coin';
};
const ISO_ANGLE = Math.PI / 6;
function toScreen(pos: Vec2, cam: Vec2, scale: number, cw: number, ch: number): Vec2 {
  const rx = pos.x - cam.x;
  const ry = pos.y - cam.y;
  const sx = (rx - ry) * Math.cos(ISO_ANGLE) * scale;
  const sy = (rx + ry) * Math.sin(ISO_ANGLE) * scale * 0.6;
  return {
    x: cw / 2 + sx,
    y: ch / 2 + sy
  };
}
function drawDiamond(ctx: CanvasRenderingContext2D, cx: number, cy: number, w: number, h: number) {
  ctx.beginPath();
  ctx.moveTo(cx, cy - h);
  ctx.lineTo(cx + w, cy);
  ctx.lineTo(cx, cy + h);
  ctx.lineTo(cx - w, cy);
  ctx.closePath();
}
const NPC_COLORS: Record<string, {
  body: string;
  glow: string;
  accent: string;
}> = {
  mordecai: {
    body: '#10b981',
    glow: 'rgba(16,185,129,0.5)',
    accent: '#065f46'
  },
  donut: {
    body: '#f472b6',
    glow: 'rgba(244,114,182,0.6)',
    accent: '#9d174d'
  },
  gnome: {
    body: '#fbbf24',
    glow: 'rgba(251,191,36,0.5)',
    accent: '#78350f'
  },
  goblin_king: {
    body: '#a855f7',
    glow: 'rgba(168,85,247,0.6)',
    accent: '#4c1d95'
  }
};
// ── PROP RENDERERS ──
function drawProp(ctx: CanvasRenderingContext2D, e: Entity, sp: Vec2, s: number, time: number) {
  const v = e.propVariant || 0;
  switch (e.propType) {
    case 'bone_pile':
      {
        ctx.fillStyle = '#d9c9a8';
        ctx.strokeStyle = '#6a5838';
        ctx.lineWidth = 1;
        // Skulls and bones in a pile
        ctx.beginPath();
        ctx.ellipse(sp.x, sp.y, s * 0.6, s * 0.25, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.beginPath();
        ctx.ellipse(sp.x - s * 0.2, sp.y - s * 0.15, s * 0.25, s * 0.2, 0.3, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.beginPath();
        ctx.ellipse(sp.x + s * 0.25, sp.y - s * 0.1, s * 0.22, s * 0.18, -0.2, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        // Eye sockets on top skull
        ctx.fillStyle = '#111';
        ctx.beginPath();
        ctx.arc(sp.x - s * 0.27, sp.y - s * 0.17, s * 0.05, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(sp.x - s * 0.15, sp.y - s * 0.17, s * 0.05, 0, Math.PI * 2);
        ctx.fill();
        return;
      }
    case 'skull':
      {
        ctx.fillStyle = '#d9c9a8';
        ctx.strokeStyle = '#6a5838';
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.ellipse(sp.x, sp.y - s * 0.2, s * 0.3, s * 0.28, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = '#111';
        ctx.beginPath();
        ctx.arc(sp.x - s * 0.1, sp.y - s * 0.25, s * 0.06, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(sp.x + s * 0.1, sp.y - s * 0.25, s * 0.06, 0, Math.PI * 2);
        ctx.fill();
        // Jaw
        ctx.strokeStyle = '#6a5838';
        ctx.beginPath();
        ctx.moveTo(sp.x - s * 0.12, sp.y - s * 0.05);
        ctx.lineTo(sp.x - s * 0.06, sp.y);
        ctx.lineTo(sp.x, sp.y - s * 0.03);
        ctx.lineTo(sp.x + s * 0.06, sp.y);
        ctx.lineTo(sp.x + s * 0.12, sp.y - s * 0.05);
        ctx.stroke();
        return;
      }
    case 'sarcophagus':
      {
        ctx.fillStyle = '#3a3548';
        ctx.strokeStyle = '#18141e';
        ctx.lineWidth = 2;
        // Base
        drawDiamond(ctx, sp.x, sp.y + s * 0.1, s * 0.9, s * 0.35);
        ctx.fill();
        ctx.stroke();
        // Top
        ctx.fillStyle = '#4a4558';
        drawDiamond(ctx, sp.x, sp.y - s * 0.3, s * 0.9, s * 0.35);
        ctx.fill();
        ctx.stroke();
        // Engraving
        ctx.strokeStyle = '#908ba0';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(sp.x - s * 0.3, sp.y - s * 0.3);
        ctx.lineTo(sp.x + s * 0.3, sp.y - s * 0.3);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(sp.x, sp.y - s * 0.3, s * 0.1, 0, Math.PI * 2);
        ctx.stroke();
        return;
      }
    case 'torch':
      {
        const flicker = Math.sin(time / 80 + (e.glowPhase || 0)) * 0.3 + 1;
        // Post
        ctx.fillStyle = '#2a1810';
        ctx.fillRect(sp.x - s * 0.08, sp.y - s * 0.8, s * 0.16, s * 0.9);
        // Flame glow (big radial)
        const grad = ctx.createRadialGradient(sp.x, sp.y - s * 0.9, 0, sp.x, sp.y - s * 0.9, s * 3 * flicker);
        grad.addColorStop(0, `rgba(255,170,60,${0.5 * flicker})`);
        grad.addColorStop(0.4, 'rgba(255,100,30,0.2)');
        grad.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = grad;
        ctx.fillRect(sp.x - s * 3, sp.y - s * 3.9, s * 6, s * 6);
        // Flame
        ctx.shadowColor = '#ff6020';
        ctx.shadowBlur = 15;
        ctx.fillStyle = '#ffb040';
        ctx.beginPath();
        ctx.moveTo(sp.x, sp.y - s * 1.3 - s * 0.2 * flicker);
        ctx.quadraticCurveTo(sp.x + s * 0.2, sp.y - s * 0.9, sp.x, sp.y - s * 0.7);
        ctx.quadraticCurveTo(sp.x - s * 0.2, sp.y - s * 0.9, sp.x, sp.y - s * 1.3 - s * 0.2 * flicker);
        ctx.fill();
        ctx.fillStyle = '#ffff80';
        ctx.beginPath();
        ctx.ellipse(sp.x, sp.y - s * 1.0, s * 0.1 * flicker, s * 0.2 * flicker, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
        return;
      }
    case 'rubble':
      {
        ctx.fillStyle = '#4a4238';
        ctx.strokeStyle = '#1a1510';
        ctx.lineWidth = 1;
        for (let i = 0; i < 4; i++) {
          const ox = (i - 1.5) * s * 0.2;
          const oy = i % 2 * s * 0.1;
          ctx.beginPath();
          ctx.moveTo(sp.x + ox - s * 0.1, sp.y + oy);
          ctx.lineTo(sp.x + ox, sp.y + oy - s * 0.15);
          ctx.lineTo(sp.x + ox + s * 0.12, sp.y + oy);
          ctx.lineTo(sp.x + ox + s * 0.08, sp.y + oy + s * 0.05);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
        }
        return;
      }
    case 'crate':
      {
        const bs = s * 0.65;
        ctx.fillStyle = '#6b4c1e';
        ctx.strokeStyle = '#3d2b0a';
        ctx.lineWidth = 1.5;
        drawDiamond(ctx, sp.x, sp.y - bs * 0.6, bs * 0.7, bs * 0.4);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = '#4a3210';
        ctx.beginPath();
        ctx.moveTo(sp.x, sp.y - bs * 0.2);
        ctx.lineTo(sp.x + bs * 0.7, sp.y - bs * 0.6);
        ctx.lineTo(sp.x + bs * 0.7, sp.y + bs * 0.1);
        ctx.lineTo(sp.x, sp.y + bs * 0.5);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = '#3a2508';
        ctx.beginPath();
        ctx.moveTo(sp.x, sp.y - bs * 0.2);
        ctx.lineTo(sp.x - bs * 0.7, sp.y - bs * 0.6);
        ctx.lineTo(sp.x - bs * 0.7, sp.y + bs * 0.1);
        ctx.lineTo(sp.x, sp.y + bs * 0.5);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        return;
      }
    case 'chain':
      {
        ctx.strokeStyle = '#4a4a58';
        ctx.lineWidth = 2;
        for (let i = 0; i < 6; i++) {
          ctx.beginPath();
          ctx.ellipse(sp.x, sp.y - s * 0.15 * i, s * 0.08, s * 0.05, i % 2 === 0 ? 0 : Math.PI / 4, 0, Math.PI * 2);
          ctx.stroke();
        }
        return;
      }
    case 'cage':
      {
        ctx.strokeStyle = '#5a4838';
        ctx.lineWidth = 2;
        // Base
        ctx.fillStyle = '#2a1810';
        drawDiamond(ctx, sp.x, sp.y + s * 0.1, s * 0.5, s * 0.2);
        ctx.fill();
        ctx.stroke();
        // Vertical bars
        for (let i = -2; i <= 2; i++) {
          ctx.beginPath();
          ctx.moveTo(sp.x + i * s * 0.2, sp.y + s * 0.1);
          ctx.lineTo(sp.x + i * s * 0.2, sp.y - s * 0.6);
          ctx.stroke();
        }
        // Top rings
        ctx.beginPath();
        ctx.ellipse(sp.x, sp.y - s * 0.6, s * 0.5, s * 0.15, 0, 0, Math.PI * 2);
        ctx.stroke();
        return;
      }
    case 'jar':
      {
        ctx.fillStyle = v === 0 ? '#60a060' : v === 1 ? '#a06060' : '#6080a0';
        ctx.strokeStyle = '#2a2a2a';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.ellipse(sp.x, sp.y - s * 0.25, s * 0.25, s * 0.32, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        // Lid
        ctx.fillStyle = '#3a2810';
        ctx.beginPath();
        ctx.ellipse(sp.x, sp.y - s * 0.55, s * 0.22, s * 0.08, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        return;
      }
    case 'bookshelf':
      {
        ctx.fillStyle = '#3a2810';
        ctx.strokeStyle = '#1a1000';
        ctx.lineWidth = 1.5;
        ctx.fillRect(sp.x - s * 0.5, sp.y - s * 1.2, s, s * 1.2);
        ctx.strokeRect(sp.x - s * 0.5, sp.y - s * 1.2, s, s * 1.2);
        // Books
        const bookColors = ['#8a3030', '#305a8a', '#3a7030', '#8a6020', '#603090'];
        for (let row = 0; row < 3; row++) {
          for (let col = 0; col < 5; col++) {
            ctx.fillStyle = bookColors[(row + col) % bookColors.length];
            ctx.fillRect(sp.x - s * 0.45 + col * s * 0.18, sp.y - s * 1.1 + row * s * 0.38, s * 0.15, s * 0.32);
          }
        }
        return;
      }
    case 'cat_bed':
      {
        ctx.fillStyle = '#e06090';
        ctx.strokeStyle = '#8a2050';
        ctx.lineWidth = 1.5;
        drawDiamond(ctx, sp.x, sp.y, s * 0.6, s * 0.25);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = '#f0a0c0';
        drawDiamond(ctx, sp.x, sp.y - s * 0.03, s * 0.45, s * 0.18);
        ctx.fill();
        return;
      }
    case 'yarn_ball':
      {
        const colors = ['#e05080', '#50a0e0', '#a0e050'];
        ctx.fillStyle = colors[v % 3];
        ctx.strokeStyle = 'rgba(0,0,0,0.3)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(sp.x, sp.y - s * 0.2, s * 0.25, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        // Yarn swirl
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        for (let a = 0; a < Math.PI * 4; a += 0.3) {
          const r = a / (Math.PI * 4) * s * 0.23;
          const x = sp.x + Math.cos(a) * r;
          const y = sp.y - s * 0.2 + Math.sin(a) * r * 0.6;
          if (a === 0) ctx.moveTo(x, y);else ctx.lineTo(x, y);
        }
        ctx.stroke();
        return;
      }
    case 'food_bowl':
      {
        ctx.fillStyle = '#8a6020';
        ctx.strokeStyle = '#3a2800';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.ellipse(sp.x, sp.y, s * 0.3, s * 0.12, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = '#603010';
        ctx.beginPath();
        ctx.ellipse(sp.x, sp.y - s * 0.04, s * 0.22, s * 0.08, 0, 0, Math.PI * 2);
        ctx.fill();
        return;
      }
    case 'rug':
      {
        ctx.fillStyle = '#8a2030';
        ctx.strokeStyle = '#5a1020';
        ctx.lineWidth = 1.5;
        drawDiamond(ctx, sp.x, sp.y, s * 1.2, s * 0.5);
        ctx.fill();
        ctx.stroke();
        ctx.strokeStyle = '#c05060';
        ctx.lineWidth = 1;
        drawDiamond(ctx, sp.x, sp.y, s * 1.05, s * 0.43);
        ctx.stroke();
        drawDiamond(ctx, sp.x, sp.y, s * 0.6, s * 0.25);
        ctx.stroke();
        return;
      }
    case 'banner':
      {
        ctx.fillStyle = '#701010';
        ctx.strokeStyle = '#300000';
        ctx.lineWidth = 1.5;
        ctx.fillRect(sp.x - s * 0.2, sp.y - s * 1.6, s * 0.4, s * 1.4);
        ctx.strokeRect(sp.x - s * 0.2, sp.y - s * 1.6, s * 0.4, s * 1.4);
        // Skull emblem
        ctx.fillStyle = '#d9c9a8';
        ctx.beginPath();
        ctx.arc(sp.x, sp.y - s * 0.9, s * 0.13, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#111';
        ctx.beginPath();
        ctx.arc(sp.x - s * 0.05, sp.y - s * 0.92, s * 0.025, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(sp.x + s * 0.05, sp.y - s * 0.92, s * 0.025, 0, Math.PI * 2);
        ctx.fill();
        return;
      }
    case 'goblin_totem':
      {
        ctx.fillStyle = '#4a3018';
        ctx.strokeStyle = '#1a1000';
        ctx.lineWidth = 1.5;
        ctx.fillRect(sp.x - s * 0.1, sp.y - s * 1.2, s * 0.2, s * 1.2);
        ctx.strokeRect(sp.x - s * 0.1, sp.y - s * 1.2, s * 0.2, s * 1.2);
        // Skull on top
        ctx.fillStyle = '#d9c9a8';
        ctx.beginPath();
        ctx.arc(sp.x, sp.y - s * 1.3, s * 0.18, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = '#ff2020';
        ctx.beginPath();
        ctx.arc(sp.x - s * 0.06, sp.y - s * 1.32, s * 0.04, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(sp.x + s * 0.06, sp.y - s * 1.32, s * 0.04, 0, Math.PI * 2);
        ctx.fill();
        return;
      }
    case 'spike_pile':
      {
        ctx.fillStyle = '#404048';
        ctx.strokeStyle = '#18181e';
        ctx.lineWidth = 1;
        for (let i = 0; i < 5; i++) {
          const ox = (i - 2) * s * 0.15;
          ctx.beginPath();
          ctx.moveTo(sp.x + ox - s * 0.06, sp.y + s * 0.1);
          ctx.lineTo(sp.x + ox, sp.y - s * 0.4 - i % 2 * s * 0.1);
          ctx.lineTo(sp.x + ox + s * 0.06, sp.y + s * 0.1);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
        }
        return;
      }
    case 'blood_stain':
      {
        ctx.fillStyle = 'rgba(90,10,15,0.6)';
        ctx.beginPath();
        ctx.ellipse(sp.x, sp.y, s * (0.5 + v * 0.15), s * (0.2 + v * 0.08), v * 0.5, 0, Math.PI * 2);
        ctx.fill();
        // Splash dots
        for (let i = 0; i < 4; i++) {
          const a = i * 1.4 + v;
          const r = s * (0.5 + Math.random() * 0.3);
          ctx.beginPath();
          ctx.arc(sp.x + Math.cos(a) * r, sp.y + Math.sin(a) * r * 0.5, s * 0.05, 0, Math.PI * 2);
          ctx.fill();
        }
        return;
      }
    case 'broken_chair':
      {
        ctx.fillStyle = '#5a3818';
        ctx.strokeStyle = '#2a1800';
        ctx.lineWidth = 1.5;
        ctx.fillRect(sp.x - s * 0.3, sp.y - s * 0.3, s * 0.6, s * 0.15);
        ctx.strokeRect(sp.x - s * 0.3, sp.y - s * 0.3, s * 0.6, s * 0.15);
        ctx.beginPath();
        ctx.moveTo(sp.x - s * 0.3, sp.y - s * 0.15);
        ctx.lineTo(sp.x - s * 0.35, sp.y + s * 0.2);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(sp.x + s * 0.3, sp.y - s * 0.15);
        ctx.lineTo(sp.x + s * 0.4, sp.y + s * 0.1);
        ctx.stroke();
        return;
      }
    case 'mirror_shard':
      {
        ctx.fillStyle = 'rgba(180,200,220,0.7)';
        ctx.strokeStyle = 'rgba(220,230,240,0.9)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(sp.x - s * 0.15, sp.y);
        ctx.lineTo(sp.x + s * 0.08, sp.y - s * 0.3);
        ctx.lineTo(sp.x + s * 0.2, sp.y - s * 0.1);
        ctx.lineTo(sp.x + s * 0.05, sp.y + s * 0.1);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        return;
      }
    case 'laundry':
      {
        ctx.fillStyle = v === 0 ? '#4060a0' : v === 1 ? '#a04060' : '#60a040';
        ctx.beginPath();
        ctx.moveTo(sp.x - s * 0.3, sp.y);
        ctx.quadraticCurveTo(sp.x - s * 0.1, sp.y - s * 0.3, sp.x + s * 0.1, sp.y - s * 0.1);
        ctx.quadraticCurveTo(sp.x + s * 0.3, sp.y - s * 0.1, sp.x + s * 0.2, sp.y + s * 0.1);
        ctx.quadraticCurveTo(sp.x, sp.y + s * 0.15, sp.x - s * 0.3, sp.y);
        ctx.fill();
        return;
      }
    case 'hearth':
      {
        ctx.fillStyle = '#2a1810';
        ctx.strokeStyle = '#1a0a00';
        ctx.lineWidth = 1.5;
        ctx.fillRect(sp.x - s * 0.8, sp.y - s * 0.8, s * 1.6, s * 0.8);
        ctx.strokeRect(sp.x - s * 0.8, sp.y - s * 0.8, s * 1.6, s * 0.8);
        // Opening
        ctx.fillStyle = '#000';
        ctx.fillRect(sp.x - s * 0.3, sp.y - s * 0.5, s * 0.6, s * 0.5);
        // Fire glow
        const glow = ctx.createRadialGradient(sp.x, sp.y - s * 0.2, 0, sp.x, sp.y - s * 0.2, s * 2);
        glow.addColorStop(0, 'rgba(255,140,40,0.6)');
        glow.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = glow;
        ctx.fillRect(sp.x - s * 2, sp.y - s * 2.2, s * 4, s * 4);
        // Flames
        ctx.fillStyle = '#ffa040';
        ctx.shadowColor = '#ff6020';
        ctx.shadowBlur = 10;
        const flicker = Math.sin(time / 100) * 0.1 + 1;
        ctx.beginPath();
        ctx.moveTo(sp.x - s * 0.15, sp.y - s * 0.1);
        ctx.quadraticCurveTo(sp.x - s * 0.1, sp.y - s * 0.45 * flicker, sp.x, sp.y - s * 0.3);
        ctx.quadraticCurveTo(sp.x + s * 0.1, sp.y - s * 0.45 * flicker, sp.x + s * 0.15, sp.y - s * 0.1);
        ctx.closePath();
        ctx.fill();
        ctx.shadowBlur = 0;
        return;
      }
    case 'herb_bundle':
      {
        ctx.strokeStyle = '#3a5010';
        ctx.lineWidth = 2;
        ctx.fillStyle = '#508020';
        for (let i = 0; i < 5; i++) {
          const a = -Math.PI / 2 + (i - 2) * 0.3;
          ctx.beginPath();
          ctx.moveTo(sp.x, sp.y);
          ctx.lineTo(sp.x + Math.cos(a) * s * 0.4, sp.y + Math.sin(a) * s * 0.4);
          ctx.stroke();
        }
        ctx.fillStyle = '#4a2818';
        ctx.fillRect(sp.x - s * 0.05, sp.y - s * 0.05, s * 0.1, s * 0.1);
        return;
      }
    case 'coin_pile':
      {
        ctx.shadowColor = '#ffd700';
        ctx.shadowBlur = 8;
        for (let i = 0; i < 5; i++) {
          ctx.fillStyle = '#ffd700';
          ctx.strokeStyle = '#8a6800';
          ctx.lineWidth = 0.5;
          ctx.beginPath();
          ctx.ellipse(sp.x + (i - 2) * s * 0.08, sp.y - i % 3 * s * 0.05, s * 0.1, s * 0.04, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        }
        ctx.shadowBlur = 0;
        return;
      }
    case 'shop_counter':
      {
        ctx.fillStyle = '#5a3818';
        ctx.strokeStyle = '#2a1800';
        ctx.lineWidth = 2;
        drawDiamond(ctx, sp.x, sp.y, s * 1.4, s * 0.45);
        ctx.fill();
        ctx.stroke();
        drawDiamond(ctx, sp.x, sp.y - s * 0.3, s * 1.4, s * 0.3);
        ctx.fill();
        ctx.stroke();
        // Top
        ctx.fillStyle = '#7a5828';
        drawDiamond(ctx, sp.x, sp.y - s * 0.3, s * 1.4, s * 0.3);
        ctx.fill();
        ctx.stroke();
        return;
      }
    case 'rune_circle':
      {
        const phase = time / 2000;
        ctx.strokeStyle = `rgba(180,120,255,${0.3 + Math.sin(phase) * 0.1})`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.ellipse(sp.x, sp.y, s * 2.5, s * 1, phase, 0, Math.PI * 2);
        ctx.stroke();
        ctx.beginPath();
        ctx.ellipse(sp.x, sp.y, s * 2.0, s * 0.8, -phase, 0, Math.PI * 2);
        ctx.stroke();
        // Runes
        ctx.fillStyle = `rgba(180,120,255,${0.5 + Math.sin(phase * 2) * 0.2})`;
        ctx.font = `${s * 0.3}px serif`;
        ctx.textAlign = 'center';
        for (let i = 0; i < 8; i++) {
          const a = phase + i / 8 * Math.PI * 2;
          const rx = sp.x + Math.cos(a) * s * 2.2;
          const ry = sp.y + Math.sin(a) * s * 0.9;
          ctx.fillText(['◉', '☽', '☌', '✧', '⬡', '◈', '⊗', '⟡'][i], rx, ry);
        }
        return;
      }
    case 'dust_cloud':
      {
        // Pure atmosphere, drawn via particles
        return;
      }
    case 'altar':
      {
        ctx.fillStyle = '#4a2838';
        ctx.strokeStyle = '#1a0008';
        ctx.lineWidth = 2;
        drawDiamond(ctx, sp.x, sp.y, s * 1.0, s * 0.35);
        ctx.fill();
        ctx.stroke();
        drawDiamond(ctx, sp.x, sp.y - s * 0.3, s * 1.0, s * 0.3);
        ctx.fill();
        ctx.stroke();
        // Top item
        ctx.fillStyle = '#ff4040';
        ctx.shadowColor = '#ff0000';
        ctx.shadowBlur = 10;
        ctx.beginPath();
        ctx.arc(sp.x, sp.y - s * 0.5, s * 0.1, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
        return;
      }
    case 'throne':
      {
        ctx.fillStyle = '#d9c9a8';
        ctx.strokeStyle = '#6a5838';
        ctx.lineWidth = 1.5;
        // Seat base made of skulls
        drawDiamond(ctx, sp.x, sp.y + s * 0.2, s * 1.3, s * 0.4);
        ctx.fill();
        ctx.stroke();
        // Back
        ctx.fillStyle = '#c9b998';
        ctx.beginPath();
        ctx.moveTo(sp.x - s * 0.9, sp.y - s * 0.2);
        ctx.lineTo(sp.x - s * 1.1, sp.y - s * 1.8);
        ctx.lineTo(sp.x, sp.y - s * 2.2);
        ctx.lineTo(sp.x + s * 1.1, sp.y - s * 1.8);
        ctx.lineTo(sp.x + s * 0.9, sp.y - s * 0.2);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        // Skull decorations
        ctx.fillStyle = '#d9c9a8';
        ctx.strokeStyle = '#6a5838';
        for (let i = -2; i <= 2; i++) {
          ctx.beginPath();
          ctx.arc(sp.x + i * s * 0.4, sp.y - s * 1.8 - Math.abs(i) * s * 0.05, s * 0.15, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
          ctx.fillStyle = '#111';
          ctx.beginPath();
          ctx.arc(sp.x + i * s * 0.4 - s * 0.05, sp.y - s * 1.8 - Math.abs(i) * s * 0.05 - s * 0.02, s * 0.03, 0, Math.PI * 2);
          ctx.fill();
          ctx.beginPath();
          ctx.arc(sp.x + i * s * 0.4 + s * 0.05, sp.y - s * 1.8 - Math.abs(i) * s * 0.05 - s * 0.02, s * 0.03, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = '#d9c9a8';
        }
        return;
      }
  }
}
// ── Creature renderer ──
function drawCreature(ctx: CanvasRenderingContext2D, e: Entity, sp: Vec2, s: number, time: number) {
  const bob = Math.sin(time / 200 + (e.glowPhase || 0)) * 2;
  if (e.creatureType === 'rat') {
    // Tiny rat body
    ctx.fillStyle = '#2a1a15';
    ctx.strokeStyle = '#0a0505';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.ellipse(sp.x, sp.y, s * 0.25, s * 0.15, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    // Head
    ctx.beginPath();
    ctx.ellipse(sp.x + s * 0.15, sp.y - s * 0.04, s * 0.13, s * 0.1, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    // Tail
    ctx.beginPath();
    ctx.moveTo(sp.x - s * 0.22, sp.y);
    ctx.quadraticCurveTo(sp.x - s * 0.4, sp.y - s * 0.1, sp.x - s * 0.5, sp.y + s * 0.1);
    ctx.stroke();
    // Red eye
    ctx.fillStyle = '#ff2020';
    ctx.beginPath();
    ctx.arc(sp.x + s * 0.22, sp.y - s * 0.06, s * 0.02, 0, Math.PI * 2);
    ctx.fill();
  } else if (e.creatureType === 'bat') {
    // Flying bat
    ctx.fillStyle = '#1a0a1a';
    ctx.strokeStyle = '#4a2040';
    ctx.lineWidth = 1;
    const flap = Math.sin(time / 100) * 0.3;
    ctx.beginPath();
    ctx.moveTo(sp.x, sp.y + bob - s * 0.1);
    ctx.quadraticCurveTo(sp.x - s * 0.4, sp.y + bob - s * 0.25 - flap * s * 0.1, sp.x - s * 0.5, sp.y + bob - s * 0.05);
    ctx.lineTo(sp.x - s * 0.3, sp.y + bob);
    ctx.lineTo(sp.x - s * 0.2, sp.y + bob - s * 0.08);
    ctx.lineTo(sp.x, sp.y + bob);
    ctx.lineTo(sp.x + s * 0.2, sp.y + bob - s * 0.08);
    ctx.lineTo(sp.x + s * 0.3, sp.y + bob);
    ctx.lineTo(sp.x + s * 0.5, sp.y + bob - s * 0.05);
    ctx.quadraticCurveTo(sp.x + s * 0.4, sp.y + bob - s * 0.25 - flap * s * 0.1, sp.x, sp.y + bob - s * 0.1);
    ctx.fill();
    ctx.stroke();
    // Eyes
    ctx.fillStyle = '#ff8080';
    ctx.beginPath();
    ctx.arc(sp.x - s * 0.04, sp.y + bob - s * 0.1, s * 0.02, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(sp.x + s * 0.04, sp.y + bob - s * 0.1, s * 0.02, 0, Math.PI * 2);
    ctx.fill();
  } else if (e.creatureType === 'moth') {
    // Glowing moth
    const glow = Math.sin(time / 300 + (e.glowPhase || 0)) * 0.3 + 0.7;
    ctx.shadowColor = '#ffe080';
    ctx.shadowBlur = 10 * glow;
    ctx.fillStyle = '#fff8c0';
    const wingSpan = s * 0.3 * (1 + Math.sin(time / 80) * 0.2);
    ctx.beginPath();
    ctx.ellipse(sp.x, sp.y + bob, wingSpan, s * 0.1, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#ffe080';
    ctx.beginPath();
    ctx.ellipse(sp.x, sp.y + bob, s * 0.05, s * 0.12, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
  } else if (e.creatureType === 'beetle') {
    ctx.fillStyle = '#0a2018';
    ctx.strokeStyle = '#0a0a0a';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.ellipse(sp.x, sp.y, s * 0.2, s * 0.14, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    // Iridescent highlight
    ctx.fillStyle = '#206040';
    ctx.beginPath();
    ctx.ellipse(sp.x - s * 0.04, sp.y - s * 0.03, s * 0.08, s * 0.05, 0, 0, Math.PI * 2);
    ctx.fill();
  }
}
function drawPickup(ctx: CanvasRenderingContext2D, e: Entity, sp: Vec2, s: number, time: number) {
  const bob = Math.sin(time / 300 + (e.glowPhase || 0)) * 3;
  if (e.pickupType === 'gold') {
    ctx.shadowColor = '#ffd700';
    ctx.shadowBlur = 12 + Math.sin(time / 200) * 4;
    ctx.fillStyle = '#ffd700';
    ctx.strokeStyle = '#8a6800';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.ellipse(sp.x, sp.y + bob, s * 0.18, s * 0.18, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#fff8a0';
    ctx.font = `bold ${s * 0.22}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText('$', sp.x, sp.y + bob + s * 0.08);
    ctx.shadowBlur = 0;
  } else if (e.pickupType === 'health') {
    ctx.shadowColor = '#ff4040';
    ctx.shadowBlur = 12 + Math.sin(time / 200) * 4;
    ctx.fillStyle = '#ff4040';
    ctx.beginPath();
    ctx.arc(sp.x, sp.y + bob, s * 0.18, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.fillRect(sp.x - s * 0.03, sp.y + bob - s * 0.09, s * 0.06, s * 0.18);
    ctx.fillRect(sp.x - s * 0.09, sp.y + bob - s * 0.03, s * 0.18, s * 0.06);
    ctx.shadowBlur = 0;
  }
}
function drawEntity(ctx: CanvasRenderingContext2D, e: Entity, cam: Vec2, scale: number, cw: number, ch: number, time: number) {
  const sp = toScreen(e.pos, cam, scale, cw, ch);
  const s = e.size * scale * 0.7;
  if (e.kind === 'prop') {
    drawProp(ctx, e, sp, s, time);
    return;
  }
  if (e.kind === 'creature') {
    drawCreature(ctx, e, sp, s, time);
    return;
  }
  if (e.kind === 'pickup') {
    drawPickup(ctx, e, sp, s, time);
    return;
  }
  if (e.kind === 'portal') {
    const phase = time / 400 + (e.glowPhase || 0);
    const ringSize = s * 1.2;
    const grad = ctx.createRadialGradient(sp.x, sp.y, 0, sp.x, sp.y, ringSize * 2.5);
    grad.addColorStop(0, 'rgba(147,51,234,0.7)');
    grad.addColorStop(0.5, 'rgba(168,85,247,0.3)');
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(sp.x - ringSize * 2.5, sp.y - ringSize * 2.5, ringSize * 5, ringSize * 5);
    for (let r = 0; r < 3; r++) {
      const rs = ringSize * (0.7 + r * 0.2) + Math.sin(phase + r) * 3;
      ctx.strokeStyle = `rgba(200,150,255,${0.7 - r * 0.15})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.ellipse(sp.x, sp.y, rs, rs * 0.4, phase * (r % 2 === 0 ? 1 : -1), 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.fillStyle = `rgba(255,255,255,${0.4 + Math.sin(phase * 2) * 0.2})`;
    ctx.beginPath();
    ctx.ellipse(sp.x, sp.y, s * 0.3, s * 0.15, 0, 0, Math.PI * 2);
    ctx.fill();
    // Destination label
    if (e.portalLabel) {
      ctx.font = 'bold 11px "Share Tech Mono", monospace';
      ctx.fillStyle = '#d0b0ff';
      ctx.strokeStyle = 'rgba(0,0,0,0.9)';
      ctx.lineWidth = 3;
      ctx.textAlign = 'center';
      ctx.strokeText(`→ ${e.portalLabel.toUpperCase()}`, sp.x, sp.y - s * 1.2);
      ctx.fillText(`→ ${e.portalLabel.toUpperCase()}`, sp.x, sp.y - s * 1.2);
    }
    return;
  }
  if (e.kind === 'stairs') {
    ctx.fillStyle = '#e5e7eb';
    ctx.shadowColor = '#ffffff';
    ctx.shadowBlur = 20 + Math.sin(time / 300) * 10;
    for (let i = 0; i < 4; i++) {
      const w = s * (1 - i * 0.12);
      drawDiamond(ctx, sp.x, sp.y - i * s * 0.15, w, s * 0.15);
      ctx.fill();
    }
    ctx.shadowBlur = 0;
    ctx.font = 'bold 12px "Share Tech Mono", monospace';
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = 'rgba(0,0,0,0.9)';
    ctx.lineWidth = 3;
    ctx.textAlign = 'center';
    ctx.strokeText('▼ DESCEND', sp.x, sp.y - s * 1.3);
    ctx.fillText('▼ DESCEND', sp.x, sp.y - s * 1.3);
    return;
  }
  // Ground shadow
  ctx.fillStyle = 'rgba(0,0,0,0.4)';
  ctx.beginPath();
  ctx.ellipse(sp.x, sp.y + s * 0.3, s * 0.7, s * 0.3, 0, 0, Math.PI * 2);
  ctx.fill();
  if (e.kind === 'player') {
    // Persistent golden ground-glow
    const glow = ctx.createRadialGradient(sp.x, sp.y + s * 0.25, 0, sp.x, sp.y + s * 0.25, s * 1.6);
    glow.addColorStop(0, 'rgba(255,220,120,0.4)');
    glow.addColorStop(1, 'rgba(255,220,120,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(sp.x - s * 1.6, sp.y - s * 1.5, s * 3.2, s * 3.2);
    const bodyH = s * 2.2;
    const walkBob = Math.sin(e.walkPhase || 0) * 2;
    const flash = e.damageFlash || 0;
    const bodyColor = flash > 0 ? '#ffffff' : e.color || '#d4a574';
    // Body (taller diamond)
    ctx.fillStyle = bodyColor;
    ctx.strokeStyle = '#5a3810';
    ctx.lineWidth = 2;
    drawDiamond(ctx, sp.x, sp.y - bodyH * 0.4 + walkBob, s * 0.75, bodyH * 0.55);
    ctx.fill();
    ctx.stroke();
    // Head
    ctx.fillStyle = flash > 0 ? '#ffffff' : '#d4a574';
    ctx.beginPath();
    ctx.arc(sp.x, sp.y - bodyH * 0.85 + walkBob, s * 0.4, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    // Hair
    ctx.fillStyle = '#2a1810';
    ctx.beginPath();
    ctx.arc(sp.x, sp.y - bodyH * 0.85 + walkBob - s * 0.15, s * 0.38, Math.PI * 1.1, Math.PI * 1.9);
    ctx.closePath();
    ctx.fill();
    // Eyes (just dots)
    ctx.fillStyle = '#1a1a1a';
    ctx.beginPath();
    ctx.arc(sp.x - s * 0.12, sp.y - bodyH * 0.85 + walkBob, s * 0.05, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(sp.x + s * 0.12, sp.y - bodyH * 0.85 + walkBob, s * 0.05, 0, Math.PI * 2);
    ctx.fill();
    // Underwear
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#8090a0';
    ctx.lineWidth = 1.5;
    drawDiamond(ctx, sp.x, sp.y - bodyH * 0.05 + walkBob, s * 0.55, bodyH * 0.16);
    ctx.fill();
    ctx.stroke();
    // Bare feet
    ctx.fillStyle = '#c4956a';
    ctx.strokeStyle = '#6a4020';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.ellipse(sp.x - s * 0.22, sp.y + s * 0.15, s * 0.18, s * 0.1, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.ellipse(sp.x + s * 0.22, sp.y + s * 0.15, s * 0.18, s * 0.1, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    // Facing indicator (small arrow showing kick direction)
    if (e.facing && (Math.abs(e.facing.x) > 0.1 || Math.abs(e.facing.y) > 0.1)) {
      const fx = sp.x + e.facing.x * s * 1.1 * Math.cos(ISO_ANGLE) - e.facing.y * s * 1.1 * Math.cos(ISO_ANGLE);
      const fy = sp.y + (e.facing.x + e.facing.y) * s * 1.1 * Math.sin(ISO_ANGLE) * 0.6;
      ctx.strokeStyle = 'rgba(255,220,120,0.8)';
      ctx.fillStyle = 'rgba(255,220,120,0.6)';
      ctx.lineWidth = 2;
      const ang = Math.atan2(fy - sp.y, fx - sp.x);
      ctx.beginPath();
      ctx.moveTo(fx, fy);
      ctx.lineTo(fx - Math.cos(ang - 0.5) * s * 0.25, fy - Math.sin(ang - 0.5) * s * 0.25);
      ctx.lineTo(fx - Math.cos(ang + 0.5) * s * 0.25, fy - Math.sin(ang + 0.5) * s * 0.25);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }
    // Name label above head
    ctx.font = 'bold 11px "Share Tech Mono", monospace';
    ctx.fillStyle = 'rgba(255,220,120,0.9)';
    ctx.strokeStyle = 'rgba(0,0,0,0.9)';
    ctx.lineWidth = 3;
    ctx.textAlign = 'center';
    ctx.strokeText('CARL', sp.x, sp.y - bodyH * 1.25 + walkBob);
    ctx.fillText('CARL', sp.x, sp.y - bodyH * 1.25 + walkBob);
  } else if (e.kind === 'npc') {
    const meta = e.npcSpeaker ? NPC_COLORS[e.npcSpeaker] : null;
    const body = meta?.body || '#888';
    const glow = meta?.glow || 'rgba(150,150,150,0.4)';
    const accent = meta?.accent || '#333';
    const bodyH = s * 1.6;
    const floatY = Math.sin(time / 500 + (e.glowPhase || 0)) * 3;
    // Ground glow
    const gnd = ctx.createRadialGradient(sp.x, sp.y + s * 0.25, 0, sp.x, sp.y + s * 0.25, s * 1.4);
    gnd.addColorStop(0, glow);
    gnd.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = gnd;
    ctx.fillRect(sp.x - s * 1.4, sp.y - s * 1.2, s * 2.8, s * 2.8);
    ctx.shadowColor = glow;
    ctx.shadowBlur = 18;
    ctx.fillStyle = body;
    ctx.strokeStyle = accent;
    ctx.lineWidth = 2;
    if (e.npcSpeaker === 'donut') {
      ctx.beginPath();
      ctx.ellipse(sp.x, sp.y - s * 0.5 + floatY, s * 0.5, s * 0.4, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(sp.x - s * 0.35, sp.y - s * 0.8 + floatY);
      ctx.lineTo(sp.x - s * 0.2, sp.y - s * 1.1 + floatY);
      ctx.lineTo(sp.x - s * 0.1, sp.y - s * 0.75 + floatY);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(sp.x + s * 0.35, sp.y - s * 0.8 + floatY);
      ctx.lineTo(sp.x + s * 0.2, sp.y - s * 1.1 + floatY);
      ctx.lineTo(sp.x + s * 0.1, sp.y - s * 0.75 + floatY);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = '#fbbf24';
      ctx.fillRect(sp.x - s * 0.2, sp.y - s * 1.1 + floatY, s * 0.4, s * 0.1);
      // Eyes
      ctx.fillStyle = '#0a0a0a';
      ctx.beginPath();
      ctx.arc(sp.x - s * 0.15, sp.y - s * 0.5 + floatY, s * 0.06, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(sp.x + s * 0.15, sp.y - s * 0.5 + floatY, s * 0.06, 0, Math.PI * 2);
      ctx.fill();
    } else if (e.npcSpeaker === 'gnome') {
      ctx.beginPath();
      ctx.ellipse(sp.x, sp.y - s * 0.4 + floatY, s * 0.45, s * 0.5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = '#dc2626';
      ctx.beginPath();
      ctx.moveTo(sp.x - s * 0.3, sp.y - s * 0.8 + floatY);
      ctx.lineTo(sp.x, sp.y - s * 1.4 + floatY);
      ctx.lineTo(sp.x + s * 0.3, sp.y - s * 0.8 + floatY);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      // Beard
      ctx.fillStyle = '#e0e0e0';
      ctx.beginPath();
      ctx.ellipse(sp.x, sp.y - s * 0.2 + floatY, s * 0.2, s * 0.25, 0, 0, Math.PI * 2);
      ctx.fill();
    } else {
      drawDiamond(ctx, sp.x, sp.y - bodyH * 0.4 + floatY, s * 0.55, bodyH * 0.5);
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(sp.x, sp.y - bodyH * 0.9 + floatY, s * 0.28, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }
    ctx.shadowBlur = 0;
    ctx.font = `bold ${Math.max(10, s * 0.25)}px "Share Tech Mono", monospace`;
    ctx.fillStyle = body;
    ctx.strokeStyle = 'rgba(0,0,0,0.8)';
    ctx.lineWidth = 3;
    ctx.textAlign = 'center';
    ctx.strokeText(e.npcLabel || '', sp.x, sp.y - bodyH - 14 + floatY);
    ctx.fillText(e.npcLabel || '', sp.x, sp.y - bodyH - 14 + floatY);
  } else if (e.kind === 'boss') {
    const meta = NPC_COLORS.goblin_king;
    const bodyH = s * 2.6;
    ctx.shadowColor = meta.glow;
    ctx.shadowBlur = 25;
    ctx.fillStyle = '#2a0a2a';
    ctx.strokeStyle = meta.accent;
    ctx.lineWidth = 3;
    drawDiamond(ctx, sp.x, sp.y - bodyH * 0.4, s * 1.1, bodyH * 0.55);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#4a0a4a';
    ctx.beginPath();
    ctx.arc(sp.x, sp.y - bodyH * 0.85, s * 0.55, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#ff0080';
    ctx.shadowColor = '#ff0080';
    ctx.shadowBlur = 15;
    ctx.beginPath();
    ctx.arc(sp.x - s * 0.2, sp.y - bodyH * 0.9, s * 0.1, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(sp.x + s * 0.2, sp.y - bodyH * 0.9, s * 0.1, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.fillStyle = '#fbbf24';
    ctx.strokeStyle = '#78350f';
    ctx.lineWidth = 2;
    const cw2 = s * 0.7;
    const cy = sp.y - bodyH * 1.15;
    ctx.beginPath();
    ctx.moveTo(sp.x - cw2, cy);
    ctx.lineTo(sp.x - cw2, cy - s * 0.15);
    ctx.lineTo(sp.x - cw2 * 0.6, cy - s * 0.1);
    ctx.lineTo(sp.x - cw2 * 0.3, cy - s * 0.3);
    ctx.lineTo(sp.x, cy - s * 0.15);
    ctx.lineTo(sp.x + cw2 * 0.3, cy - s * 0.3);
    ctx.lineTo(sp.x + cw2 * 0.6, cy - s * 0.1);
    ctx.lineTo(sp.x + cw2, cy - s * 0.15);
    ctx.lineTo(sp.x + cw2, cy);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    const barW = s * 2.5;
    const barH = 6;
    const barY = sp.y - bodyH - 32;
    ctx.fillStyle = '#1a0000';
    ctx.fillRect(sp.x - barW / 2, barY, barW, barH);
    ctx.fillStyle = '#ff0040';
    ctx.fillRect(sp.x - barW / 2, barY, barW * (e.hp / e.maxHp), barH);
    ctx.strokeStyle = '#ff80c0';
    ctx.strokeRect(sp.x - barW / 2, barY, barW, barH);
    ctx.font = `bold ${Math.max(10, s * 0.28)}px "Share Tech Mono", monospace`;
    ctx.fillStyle = '#ff80c0';
    ctx.textAlign = 'center';
    ctx.fillText('THE GOBLIN KING', sp.x, barY - 6);
  } else if (e.kind === 'enemy') {
    const flash = e.damageFlash || 0;
    const bob = Math.sin(time / 180 + (e.glowPhase || 0)) * 1.5;
    if (e.isBrute) {
      const bodyH = s * 2.6;
      ctx.fillStyle = flash > 0 ? '#ffffff' : '#7a3020';
      ctx.strokeStyle = '#2a0a00';
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.ellipse(sp.x, sp.y - bodyH * 0.4 + bob, s * 1.0, bodyH * 0.45, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = flash > 0 ? '#ffffff' : '#8a4030';
      ctx.beginPath();
      ctx.arc(sp.x, sp.y - bodyH * 0.85 + bob, s * 0.55, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = '#5a1810';
      ctx.beginPath();
      ctx.arc(sp.x - s * 0.25, sp.y - bodyH * 0.75 + bob, s * 0.08, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(sp.x + s * 0.2, sp.y - bodyH * 0.95 + bob, s * 0.07, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#f0e0c0';
      ctx.strokeStyle = '#6a5838';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(sp.x - s * 0.18, sp.y - bodyH * 0.7 + bob);
      ctx.lineTo(sp.x - s * 0.25, sp.y - bodyH * 0.55 + bob);
      ctx.lineTo(sp.x - s * 0.12, sp.y - bodyH * 0.62 + bob);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(sp.x + s * 0.18, sp.y - bodyH * 0.7 + bob);
      ctx.lineTo(sp.x + s * 0.25, sp.y - bodyH * 0.55 + bob);
      ctx.lineTo(sp.x + s * 0.12, sp.y - bodyH * 0.62 + bob);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = '#ffcc20';
      ctx.shadowColor = '#ff8000';
      ctx.shadowBlur = 8;
      ctx.beginPath();
      ctx.arc(sp.x - s * 0.18, sp.y - bodyH * 0.9 + bob, s * 0.08, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(sp.x + s * 0.18, sp.y - bodyH * 0.9 + bob, s * 0.08, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.fillStyle = '#3a2010';
      ctx.strokeStyle = '#1a0a00';
      ctx.lineWidth = 2;
      const clubSwing = Math.sin(time / 300) * 0.3;
      ctx.save();
      ctx.translate(sp.x + s * 0.7, sp.y - bodyH * 0.3 + bob);
      ctx.rotate(clubSwing);
      ctx.fillRect(-s * 0.08, -s * 0.6, s * 0.16, s * 0.8);
      ctx.beginPath();
      ctx.arc(0, -s * 0.7, s * 0.22, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
      ctx.font = 'bold 10px "Share Tech Mono", monospace';
      ctx.fillStyle = '#ff6040';
      ctx.strokeStyle = 'rgba(0,0,0,0.9)';
      ctx.lineWidth = 3;
      ctx.textAlign = 'center';
      ctx.strokeText('BRUTE', sp.x, sp.y - bodyH - 10);
      ctx.fillText('BRUTE', sp.x, sp.y - bodyH - 10);
    } else if (e.isAssassin) {
      const bodyH = s * 1.9;
      ctx.fillStyle = flash > 0 ? '#ffffff' : '#1a0a24';
      ctx.strokeStyle = '#5a2080';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(sp.x, sp.y - bodyH + bob);
      ctx.lineTo(sp.x - s * 0.55, sp.y + bob);
      ctx.lineTo(sp.x + s * 0.55, sp.y + bob);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = '#000';
      ctx.beginPath();
      ctx.ellipse(sp.x, sp.y - bodyH * 0.75 + bob, s * 0.22, s * 0.18, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#c040ff';
      ctx.shadowColor = '#c040ff';
      ctx.shadowBlur = 10;
      ctx.beginPath();
      ctx.arc(sp.x - s * 0.08, sp.y - bodyH * 0.75 + bob, s * 0.04, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(sp.x + s * 0.08, sp.y - bodyH * 0.75 + bob, s * 0.04, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.fillStyle = '#c040ff';
      ctx.shadowColor = '#c040ff';
      ctx.shadowBlur = 6;
      ctx.beginPath();
      ctx.moveTo(sp.x + s * 0.4, sp.y - bodyH * 0.3 + bob);
      ctx.lineTo(sp.x + s * 0.6, sp.y - bodyH * 0.45 + bob);
      ctx.lineTo(sp.x + s * 0.5, sp.y - bodyH * 0.2 + bob);
      ctx.closePath();
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.font = 'bold 10px "Share Tech Mono", monospace';
      ctx.fillStyle = '#c040ff';
      ctx.strokeStyle = 'rgba(0,0,0,0.9)';
      ctx.lineWidth = 3;
      ctx.textAlign = 'center';
      ctx.strokeText('ASSASSIN', sp.x, sp.y - bodyH - 8);
      ctx.fillText('ASSASSIN', sp.x, sp.y - bodyH - 8);
    } else {
      const bodyH = s * 1.7;
      ctx.fillStyle = flash > 0 ? '#ffffff' : '#4a7030';
      ctx.strokeStyle = '#1a3010';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.ellipse(sp.x, sp.y - bodyH * 0.3 + bob, s * 0.55, bodyH * 0.35, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = flash > 0 ? '#ffffff' : '#5a8040';
      ctx.beginPath();
      ctx.arc(sp.x, sp.y - bodyH * 0.75 + bob, s * 0.42, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = flash > 0 ? '#ffffff' : '#4a7030';
      ctx.beginPath();
      ctx.moveTo(sp.x - s * 0.38, sp.y - bodyH * 0.75 + bob);
      ctx.lineTo(sp.x - s * 0.62, sp.y - bodyH * 0.95 + bob);
      ctx.lineTo(sp.x - s * 0.32, sp.y - bodyH * 0.7 + bob);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(sp.x + s * 0.38, sp.y - bodyH * 0.75 + bob);
      ctx.lineTo(sp.x + s * 0.62, sp.y - bodyH * 0.95 + bob);
      ctx.lineTo(sp.x + s * 0.32, sp.y - bodyH * 0.7 + bob);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = '#ff2020';
      ctx.shadowColor = '#ff2020';
      ctx.shadowBlur = 8;
      ctx.beginPath();
      ctx.arc(sp.x - s * 0.14, sp.y - bodyH * 0.78 + bob, s * 0.07, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(sp.x + s * 0.14, sp.y - bodyH * 0.78 + bob, s * 0.07, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.fillStyle = '#f0e0c0';
      ctx.beginPath();
      ctx.moveTo(sp.x - s * 0.1, sp.y - bodyH * 0.55 + bob);
      ctx.lineTo(sp.x - s * 0.07, sp.y - bodyH * 0.45 + bob);
      ctx.lineTo(sp.x - s * 0.04, sp.y - bodyH * 0.55 + bob);
      ctx.closePath();
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(sp.x + s * 0.04, sp.y - bodyH * 0.55 + bob);
      ctx.lineTo(sp.x + s * 0.07, sp.y - bodyH * 0.45 + bob);
      ctx.lineTo(sp.x + s * 0.1, sp.y - bodyH * 0.55 + bob);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = '#4a2810';
      ctx.strokeStyle = '#1a0a00';
      ctx.lineWidth = 1.5;
      const swing = Math.sin(time / 220 + (e.glowPhase || 0)) * 0.4;
      ctx.save();
      ctx.translate(sp.x + s * 0.4, sp.y - bodyH * 0.2 + bob);
      ctx.rotate(swing);
      ctx.fillRect(-s * 0.05, -s * 0.35, s * 0.1, s * 0.5);
      ctx.beginPath();
      ctx.moveTo(-s * 0.12, -s * 0.4);
      ctx.lineTo(0, -s * 0.55);
      ctx.lineTo(s * 0.12, -s * 0.4);
      ctx.lineTo(s * 0.08, -s * 0.3);
      ctx.lineTo(-s * 0.08, -s * 0.3);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    }
    if (e.hp < e.maxHp) {
      const bodyH2 = s * (e.isBrute ? 2.6 : e.isAssassin ? 1.9 : 1.7);
      const barW = s * 1.4;
      const barH = 4;
      const barY = sp.y - bodyH2 - 6;
      ctx.fillStyle = 'rgba(20,0,0,0.9)';
      ctx.fillRect(sp.x - barW / 2, barY, barW, barH);
      ctx.fillStyle = '#ff3333';
      ctx.fillRect(sp.x - barW / 2, barY, barW * (e.hp / e.maxHp), barH);
      ctx.strokeStyle = 'rgba(0,0,0,0.8)';
      ctx.lineWidth = 1;
      ctx.strokeRect(sp.x - barW / 2, barY, barW, barH);
    }
  } else if (e.kind === 'crate' || e.kind === 'sponsor') {
    const bs = s * 0.8;
    if (e.kind === 'sponsor') {
      ctx.shadowColor = '#ffd700';
      ctx.shadowBlur = 15;
      ctx.fillStyle = '#ffd700';
      ctx.strokeStyle = '#b8860b';
      ctx.lineWidth = 2;
      drawDiamond(ctx, sp.x, sp.y - bs * 0.5, bs * 0.7, bs * 0.5);
      ctx.fill();
      ctx.stroke();
      ctx.shadowBlur = 0;
      ctx.fillStyle = '#fff';
      ctx.font = `${s * 0.5}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText('★', sp.x, sp.y - bs * 0.35);
    } else {
      ctx.fillStyle = e.color;
      ctx.strokeStyle = '#3d2b0a';
      ctx.lineWidth = 1.5;
      drawDiamond(ctx, sp.x, sp.y - bs * 0.8, bs * 0.7, bs * 0.4);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = '#4a3210';
      ctx.beginPath();
      ctx.moveTo(sp.x, sp.y - bs * 0.4);
      ctx.lineTo(sp.x + bs * 0.7, sp.y - bs * 0.8);
      ctx.lineTo(sp.x + bs * 0.7, sp.y - bs * 0.1);
      ctx.lineTo(sp.x, sp.y + bs * 0.3);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = '#3a2508';
      ctx.beginPath();
      ctx.moveTo(sp.x, sp.y - bs * 0.4);
      ctx.lineTo(sp.x - bs * 0.7, sp.y - bs * 0.8);
      ctx.lineTo(sp.x - bs * 0.7, sp.y - bs * 0.1);
      ctx.lineTo(sp.x, sp.y + bs * 0.3);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }
  }
}
export function BabylonScene() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.focus();
    const ctx = canvas.getContext('2d')!;
    let animId = 0;
    let entities: Entity[] = [];
    let particles: Particle[] = [];
    let cam: Vec2 = v2(0, 0);
    let camShake = 0;
    let hitStopTimer = 0;
    let scale = 32;
    let scaleTarget = 32;
    let zoneEnterFlash = 0;
    let zoneEnterColor = '#ffffff';
    let currentZoneId: ZoneId = 'awakening';
    let flashTimer = 0;
    let mordecaiTimer = 0;
    let ambientMoteTimer = 0;
    let lastKillTime = 0;
    let killsInWindow = 0;
    let idleTime = 0;
    let lowViewershipTime = 0;
    let highViewershipTime = 0;
    let lastSponsorDrop = 0;
    let lowHealthWarned = false;
    let firstKillDone = false;
    let lastStompTime = 0;
    let lastKickTime = 0;
    let lastInteractTime = 0;
    let damageInstances = 0;
    const inputMap: Record<string, boolean> = {};
    const onKeyDown = (e: KeyboardEvent) => {
      inputMap[e.key.toLowerCase()] = true;
    };
    const onKeyUp = (e: KeyboardEvent) => {
      inputMap[e.key.toLowerCase()] = false;
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    function makeEntity(kind: EntityKind, pos: Vec2, overrides: Partial<Entity> = {}): Entity {
      return {
        id: `${kind}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        pos,
        vel: v2(0, 0),
        size: 1,
        mass: 1,
        color: '#888',
        kind,
        hp: 30,
        maxHp: 30,
        isBrute: false,
        isAssassin: false,
        speed: 4,
        lastDamageToPlayer: 0,
        dead: false,
        friction: 0.88,
        ...overrides
      };
    }
    function getPlayer(): Entity | undefined {
      return entities.find(e => e.kind === 'player' && !e.dead);
    }
    function addParticle(p: Particle) {
      particles.push(p);
    }
    function spawnDustPuff(pos: Vec2, count = 8, color = 'rgba(180,160,140,0.7)') {
      for (let i = 0; i < count; i++) {
        const a = Math.random() * Math.PI * 2;
        const sp = 2 + Math.random() * 3;
        addParticle({
          pos: {
            ...pos
          },
          vel: {
            x: Math.cos(a) * sp,
            y: Math.sin(a) * sp
          },
          life: 0.4 + Math.random() * 0.3,
          maxLife: 0.7,
          color,
          size: 4 + Math.random() * 3,
          kind: 'dust'
        });
      }
    }
    function spawnBloodSplatter(pos: Vec2) {
      for (let i = 0; i < 12; i++) {
        const a = Math.random() * Math.PI * 2;
        const sp = 3 + Math.random() * 5;
        addParticle({
          pos: {
            ...pos
          },
          vel: {
            x: Math.cos(a) * sp,
            y: Math.sin(a) * sp
          },
          life: 0.5 + Math.random() * 0.4,
          maxLife: 0.9,
          color: '#8a1020',
          size: 3 + Math.random() * 3,
          kind: 'blood'
        });
      }
    }
    function spawnCoinBurst(pos: Vec2, count = 6) {
      for (let i = 0; i < count; i++) {
        const a = -Math.PI / 2 + (Math.random() - 0.5) * 1.5;
        const sp = 4 + Math.random() * 3;
        addParticle({
          pos: {
            ...pos
          },
          vel: {
            x: Math.cos(a) * sp,
            y: Math.sin(a) * sp
          },
          life: 0.8,
          maxLife: 0.8,
          color: '#ffd700',
          size: 4,
          kind: 'coin'
        });
      }
    }
    function spawnImpactRing(pos: Vec2, color = 'rgba(255,220,120,0.8)') {
      addParticle({
        pos: {
          ...pos
        },
        vel: {
          x: 0,
          y: 0
        },
        life: 0.4,
        maxLife: 0.4,
        color,
        size: 0,
        kind: 'ring'
      });
    }
    function spawnEnemyAt(pos: Vec2, variant: 'grunt' | 'brute' | 'assassin' = 'grunt') {
      const isBrute = variant === 'brute';
      const isAssassin = variant === 'assassin';
      entities.push(makeEntity('enemy', pos, {
        color: isAssassin ? '#7b2d8e' : isBrute ? '#5c1010' : '#a01515',
        size: isBrute ? 1.6 : isAssassin ? 1.1 : 1.1,
        mass: isBrute ? 3 : 1,
        hp: isBrute ? 60 : isAssassin ? 40 : 30,
        maxHp: isBrute ? 60 : isAssassin ? 40 : 30,
        isBrute,
        isAssassin,
        speed: isAssassin ? 7 : isBrute ? 2.5 : 4,
        friction: 0.9
      }));
    }
    function spawnBoss(pos: Vec2) {
      entities.push(makeEntity('boss', pos, {
        color: '#4a0a4a',
        size: 3.2,
        mass: 10,
        hp: 300,
        maxHp: 300,
        isBrute: true,
        speed: 2.5,
        friction: 0.85
      }));
    }
    function loadZone(zoneId: ZoneId) {
      const zone = ZONES[zoneId];
      if (!zone) return;
      const player = getPlayer();
      const prevZoneId = currentZoneId;
      entities = entities.filter(e => e.kind === 'player');
      if (player) {
        let sx = zone.spawnPoint.x;
        let sz = zone.spawnPoint.z;
        const returnExit = zone.exits.find(ex => ex.to === prevZoneId);
        if (returnExit && prevZoneId !== zoneId) {
          const targetZone = ZONES[returnExit.to];
          const dir = v2Norm(v2Sub(v2(targetZone.x, targetZone.z), v2(zone.x, zone.z)));
          const edgeDist = zone.size - 2;
          const portalX = zone.x + dir.x * edgeDist;
          const portalZ = zone.z + dir.y * edgeDist;
          const into = v2Norm(v2Sub(v2(zone.x, zone.z), v2(portalX, portalZ)));
          sx = portalX + into.x * 4.5;
          sz = portalZ + into.y * 4.5;
        }
        player.pos = v2(sx, sz);
        player.vel = v2(0, 0);
        cam = v2(sx, sz);
      }
      currentZoneId = zoneId;
      gameStore.setState({
        currentZone: zoneId
      });
      gameStore.showZoneBanner(zone.name, zone.subtitle);
      // Zone enter juice
      scale = 26;
      scaleTarget = 32;
      zoneEnterFlash = 0.5;
      zoneEnterColor = zone.accentColor;
      spawnDustPuff(v2(zone.spawnPoint.x, zone.spawnPoint.z), 14, 'rgba(220,200,160,0.5)');
      spawnImpactRing(v2(zone.spawnPoint.x, zone.spawnPoint.z), `rgba(255,220,160,0.7)`);
      // Portals with labels
      zone.exits.forEach((exit, i) => {
        const targetZone = ZONES[exit.to];
        const dir = v2Norm(v2Sub(v2(targetZone.x, targetZone.z), v2(zone.x, zone.z)));
        const edgeDist = zone.size - 2;
        const portalPos = v2(zone.x + dir.x * edgeDist, zone.z + dir.y * edgeDist);
        entities.push(makeEntity('portal', portalPos, {
          color: '#a855f7',
          size: 1.5,
          mass: 9999,
          friction: 1,
          portalTarget: exit.to,
          portalLabel: targetZone.name,
          glowPhase: i * 1.5
        }));
      });
      // Props
      zone.props.forEach((p: PropDef) => {
        entities.push(makeEntity('prop', v2(zone.x + p.x, zone.z + p.z), {
          color: '#888',
          size: 1,
          mass: p.breakable ? 3 : 9999,
          friction: 1,
          propType: p.type,
          breakable: p.breakable,
          dropsGold: p.dropsGold,
          dropsHp: p.dropsHp,
          propVariant: p.variant,
          glowPhase: Math.random() * Math.PI * 2,
          hp: p.breakable ? 1 : 9999,
          maxHp: 1
        }));
      });
      // Creatures
      zone.creatures.forEach(cd => {
        for (let i = 0; i < cd.count; i++) {
          const angle = Math.random() * Math.PI * 2;
          const dist = Math.random() * zone.size * 0.8;
          entities.push(makeEntity('creature', v2(zone.x + Math.cos(angle) * dist, zone.z + Math.sin(angle) * dist), {
            color: '#333',
            size: 0.7,
            mass: 0.3,
            friction: 0.9,
            creatureType: cd.type,
            wanderTarget: v2(zone.x + (Math.random() - 0.5) * zone.size * 1.6, zone.z + (Math.random() - 0.5) * zone.size * 1.6),
            wanderTimer: 2 + Math.random() * 3,
            glowPhase: Math.random() * Math.PI * 2,
            speed: cd.type === 'rat' ? 6 : cd.type === 'bat' ? 4 : 2
          }));
        }
      });
      // Zone enemies
      if (zone.kind === 'combat' && zone.enemyTypes) {
        zone.enemyTypes.forEach((variant, i) => {
          const angle = i / zone.enemyTypes!.length * Math.PI * 2;
          const dist = zone.size * 0.6;
          const pos = v2(zone.x + Math.cos(angle) * dist, zone.z + Math.sin(angle) * dist);
          spawnEnemyAt(pos, variant as any);
        });
      }
      if (zone.kind === 'boss') spawnBoss(v2(zone.x + 6, zone.z));
      // Extra crates in combat zones (kept for physics fun)
      if (zone.kind === 'combat') {
        for (let i = 0; i < 5; i++) {
          const angle = Math.random() * Math.PI * 2;
          const dist = Math.random() * zone.size * 0.5;
          entities.push(makeEntity('crate', v2(zone.x + Math.cos(angle) * dist, zone.z + Math.sin(angle) * dist), {
            color: '#6b4c1e',
            size: 1.1,
            mass: 2,
            hp: 1,
            maxHp: 1,
            friction: 0.88
          }));
        }
      }
      // NPCs
      if (zoneId === 'first_breath') {
        entities.push(makeEntity('npc', v2(zone.x + 4, zone.z), {
          color: '#10b981',
          size: 1.1,
          mass: 9999,
          friction: 1,
          npcSpeaker: 'mordecai',
          npcLabel: 'MORDECAI',
          glowPhase: 0
        }));
      }
      if (zoneId === 'donut_discovery') {
        entities.push(makeEntity('npc', v2(zone.x + 3, zone.z + 2), {
          color: '#f472b6',
          size: 0.9,
          mass: 9999,
          friction: 1,
          npcSpeaker: 'donut',
          npcLabel: 'PRINCESS DONUT',
          glowPhase: 1
        }));
      }
      if (zoneId === 'merchant_nook') {
        entities.push(makeEntity('npc', v2(zone.x, zone.z + 4), {
          color: '#fbbf24',
          size: 1.0,
          mass: 9999,
          friction: 1,
          npcSpeaker: 'gnome',
          npcLabel: 'MRS. WARTBOTTOM',
          glowPhase: 2
        }));
      }
      if (zoneId === 'throne_of_bones' && gameStore.hasStoryFlag('boss_defeated')) {
        entities.push(makeEntity('stairs', v2(zone.x + 10, zone.z), {
          color: '#ffffff',
          size: 1.5,
          mass: 9999,
          friction: 1
        }));
      }
      const visited = gameStore.getState().visitedZones;
      if (!visited.includes(zoneId)) {
        gameStore.setState({
          visitedZones: [...visited, zoneId]
        });
        const beatKey = `${zoneId}_entry`;
        if (storyBeats[beatKey]) {
          setTimeout(() => gameStore.queueDialogue(storyBeats[beatKey]), 800);
        }
      }
    }
    function killEnemy(e: Entity, isEnvironmental: boolean) {
      e.dead = true;
      const now = Date.now();
      const state = gameStore.getState();
      flashTimer = 0.08;
      camShake = 8;
      hitStopTimer = 0.06;
      spawnBloodSplatter(e.pos);
      spawnImpactRing(e.pos);
      let viewGain = isEnvironmental ? 15 : 8;
      if (state.isBarefoot) viewGain *= 1.1;
      if (now - lastKillTime < 3000) {
        killsInWindow++;
        if (killsInWindow >= 2) {
          viewGain += 10;
          if (Math.random() < 0.4) gameStore.addMessage(getQuip('onMultiKill'), 'loot');
        }
      } else {
        killsInWindow = 1;
      }
      lastKillTime = now;
      const newKills = state.kills + 1;
      gameStore.setState({
        kills: newKills,
        viewership: Math.min(100, state.viewership + viewGain),
        gold: state.gold + (e.isBrute ? 3 : 1)
      });
      if (!firstKillDone) {
        firstKillDone = true;
        gameStore.addMessage(quips.firstKill, 'achievement');
      } else if (Math.random() < 0.2) {
        gameStore.addMessage(isEnvironmental ? getQuip('onEnvironmentalKill') : getQuip('onKill'), 'system');
      }
    }
    function killBoss(e: Entity) {
      e.dead = true;
      flashTimer = 0.5;
      camShake = 20;
      hitStopTimer = 0.2;
      spawnBloodSplatter(e.pos);
      spawnBloodSplatter(e.pos);
      spawnImpactRing(e.pos, 'rgba(255,100,200,0.9)');
      gameStore.setStoryFlag('boss_defeated');
      gameStore.setState({
        kills: gameStore.getState().kills + 1,
        viewership: 100,
        gold: gameStore.getState().gold + 50
      });
      gameStore.queueDialogue(storyBeats.throne_cleared);
      entities.push(makeEntity('stairs', v2(e.pos.x + 4, e.pos.y), {
        color: '#ffffff',
        size: 1.5,
        mass: 9999,
        friction: 1
      }));
    }
    function breakProp(e: Entity) {
      e.dead = true;
      spawnDustPuff(e.pos, 10);
      camShake = Math.max(camShake, 4);
      if (e.dropsGold) {
        // Spawn gold pickups
        for (let i = 0; i < Math.min(e.dropsGold, 4); i++) {
          const a = Math.random() * Math.PI * 2;
          const r = 0.3 + Math.random() * 0.8;
          entities.push(makeEntity('pickup', v2(e.pos.x + Math.cos(a) * r, e.pos.y + Math.sin(a) * r), {
            color: '#ffd700',
            size: 0.5,
            mass: 0.5,
            friction: 0.8,
            pickupType: 'gold',
            pickupValue: Math.ceil(e.dropsGold / Math.min(e.dropsGold, 4)),
            glowPhase: Math.random() * Math.PI * 2
          }));
        }
        spawnCoinBurst(e.pos, e.dropsGold);
      }
      if (e.dropsHp) {
        entities.push(makeEntity('pickup', v2(e.pos.x, e.pos.y), {
          color: '#ff4040',
          size: 0.5,
          mass: 0.5,
          friction: 0.8,
          pickupType: 'health',
          pickupValue: e.dropsHp
        }));
      }
    }
    function handlePlayerDeath() {
      const player = getPlayer();
      if (!player) return;
      const enemies = entities.filter(e => (e.kind === 'enemy' || e.kind === 'boss') && !e.dead);
      const nearbyEnemies = enemies.filter(e => v2Dist(e.pos, player.pos) < 5).length;
      const killedByBoss = enemies.some(e => e.kind === 'boss' && v2Dist(e.pos, player.pos) < 5);
      let cause = '';
      if (killedByBoss) cause = 'Killed by: The Goblin King. Your crown ambitions were premature.';else if (nearbyEnemies >= 3) cause = `Swarmed by ${nearbyEnemies} hostiles. Situational awareness: nonexistent.`;else if (damageInstances > 20) cause = 'Death by a thousand cuts. Literally closer to forty, but who is counting.';else cause = `Killed by: Generic Grunt #${Math.floor(Math.random() * 900 + 100)}. Unremarkable.`;
      player.dead = true;
      gameStore.setState({
        isGameOver: true,
        deathCause: cause
      });
      gameStore.addMessage('PATHETIC DISPLAY. TRULY PATHETIC.', 'roast');
    }
    let kickArcTimer = 0;
    let kickArcDir: Vec2 = v2(0, 1);
    function performKick() {
      const player = getPlayer();
      if (!player) return;
      const kickRadius = 5;
      const kickForce = 22;
      kickArcTimer = 0.25;
      kickArcDir = player.facing || v2(0, 1);
      let hitSomething = false;
      const state = gameStore.getState();
      // Visual kick feedback
      spawnDustPuff(player.pos, 6);
      camShake = Math.max(camShake, 3);
      entities.forEach(e => {
        if (e === player || e.dead) return;
        if (e.kind === 'pillar' || e.kind === 'npc' || e.kind === 'portal' || e.kind === 'stairs' || e.kind === 'creature' || e.kind === 'pickup') return;
        const dist = v2Dist(player.pos, e.pos);
        if (dist < kickRadius) {
          const dir = v2Norm(v2Sub(e.pos, player.pos));
          let force = kickForce;
          if (e.kind === 'boss') force = kickForce * 0.3;else if (e.isBrute) force = kickForce * 0.6;else if (e.kind === 'crate' || e.kind === 'sponsor') force = kickForce * 1.5;else if (e.kind === 'prop' && e.breakable) force = kickForce * 1.2;
          e.vel = v2Add(e.vel, v2Scale(dir, force / Math.max(e.mass, 0.5)));
          hitSomething = true;
          if (e.kind === 'enemy') {
            e.hp -= state.isBarefoot ? 16.5 : 15;
            e.damageFlash = 0.1;
          }
          if (e.kind === 'boss') {
            e.hp -= state.isBarefoot ? 44 : 40;
          }
          if (e.kind === 'prop' && e.breakable) breakProp(e);
        }
      });
      if (hitSomething) {
        if (!gameStore.hasStoryFlag('has_kicked')) gameStore.setStoryFlag('has_kicked');
        spawnImpactRing(player.pos);
      }
      if (hitSomething && state.isBarefoot && Math.random() < 0.15) {
        gameStore.addMessage(getQuip('onBarefootAction'), 'info');
      }
    }
    function performStomp() {
      const player = getPlayer();
      if (!player) return;
      const stompRadius = 2.5;
      const stompForce = 12;
      const state = gameStore.getState();
      spawnDustPuff(player.pos, 10);
      spawnImpactRing(player.pos, 'rgba(200,140,80,0.8)');
      camShake = Math.max(camShake, 4);
      entities.forEach(e => {
        if (e === player || e.dead) return;
        if (e.kind === 'pillar' || e.kind === 'npc' || e.kind === 'portal' || e.kind === 'stairs' || e.kind === 'creature' || e.kind === 'pickup') return;
        if (v2Dist(player.pos, e.pos) < stompRadius) {
          const dir = v2Norm(v2Sub(e.pos, player.pos));
          e.vel = v2Add(e.vel, v2Scale(dir, stompForce / Math.max(e.mass, 0.5)));
          if (e.kind === 'enemy') {
            e.hp -= state.isBarefoot ? 11 : 10;
            e.damageFlash = 0.1;
          }
          if (e.kind === 'boss') e.hp -= state.isBarefoot ? 22 : 20;
          if (e.kind === 'prop' && e.breakable) breakProp(e);
        }
      });
    }
    function tryInteract(): boolean {
      const player = getPlayer();
      if (!player) return false;
      const interactRadius = 3;
      let closestPortal: Entity | null = null;
      let closestPortalDist = Infinity;
      let closestStairs: Entity | null = null;
      let closestStairsDist = Infinity;
      let closestNpc: Entity | null = null;
      let closestNpcDist = Infinity;
      entities.forEach(e => {
        if (e.dead) return;
        const d = v2Dist(player.pos, e.pos);
        if (e.kind === 'portal' && d < interactRadius && d < closestPortalDist) {
          closestPortal = e;
          closestPortalDist = d;
        }
        if (e.kind === 'stairs' && d < interactRadius && d < closestStairsDist) {
          closestStairs = e;
          closestStairsDist = d;
        }
        if (e.kind === 'npc' && d < interactRadius && d < closestNpcDist) {
          closestNpc = e;
          closestNpcDist = d;
        }
      });
      if (closestStairs) {
        if (!gameStore.hasStoryFlag('has_interacted')) gameStore.setStoryFlag('has_interacted');
        gameStore.queueDialogue(storyBeats.stairs_down);
        setTimeout(() => gameStore.setState({
          phase: 'floor_complete'
        }), 2000);
        return true;
      }
      if (closestNpc) {
        if (!gameStore.hasStoryFlag('has_interacted')) gameStore.setStoryFlag('has_interacted');
        const npc = closestNpc as Entity;
        if (npc.npcSpeaker === 'gnome') {
          gameStore.setState({
            shopOpen: true
          });
          return true;
        }
        if (npc.npcSpeaker === 'mordecai') gameStore.queueDialogue([{
          speaker: 'mordecai',
          text: mordecaiAmbient[Math.floor(Math.random() * mordecaiAmbient.length)],
          duration: 4000
        }]);else if (npc.npcSpeaker === 'donut') {
          const lines = ['Carl, you are embarrassing me.', 'I demand treats. Appropriate treats.', 'Did the cameras get my good side?'];
          gameStore.queueDialogue([{
            speaker: 'donut',
            text: lines[Math.floor(Math.random() * lines.length)],
            duration: 4000
          }]);
        }
        return true;
      }
      if (closestPortal) {
        const target = (closestPortal as Entity).portalTarget;
        if (target) {
          loadZone(target);
          if (!gameStore.hasStoryFlag('has_interacted')) gameStore.setStoryFlag('has_interacted');
          return true;
        }
      }
      return false;
    }
    function handlePhaseChange() {
      const phase = gameStore.getState().phase;
      if (phase === 'playing' && entities.length === 0) {
        entities.push(makeEntity('player', v2(0, 0), {
          color: '#d4a574',
          size: 1.3,
          mass: 1,
          hp: 100,
          maxHp: 100,
          speed: 8,
          friction: 0.85,
          facing: v2(0, 1),
          walkPhase: 0
        }));
        loadZone('awakening');
      }
    }
    const unsub = gameStore.subscribe(s => {
      if (s.phase === 'playing' && !getPlayer()) handlePhaseChange();
    });
    const resetHandler = () => {
      entities = [];
      particles = [];
      currentZoneId = 'awakening';
      lastKillTime = 0;
      killsInWindow = 0;
      idleTime = 0;
      lowViewershipTime = 0;
      highViewershipTime = 0;
      lastSponsorDrop = 0;
      lowHealthWarned = false;
      firstKillDone = false;
      damageInstances = 0;
      lastStompTime = 0;
      lastKickTime = 0;
      lastInteractTime = 0;
      flashTimer = 0;
      mordecaiTimer = 0;
      camShake = 0;
      hitStopTimer = 0;
      entities.push(makeEntity('player', v2(0, 0), {
        color: '#d4a574',
        size: 1.3,
        mass: 1,
        hp: 100,
        maxHp: 100,
        speed: 8,
        friction: 0.85,
        facing: v2(0, 1),
        walkPhase: 0
      }));
      loadZone('awakening');
    };
    window.addEventListener('reset-game', resetHandler);
    if (gameStore.getState().phase === 'playing') handlePhaseChange();
    let lastTime = performance.now();
    function gameLoop(timestamp: number) {
      animId = requestAnimationFrame(gameLoop);
      const rawDt = (timestamp - lastTime) / 1000;
      const dt = Math.min(rawDt, 0.05);
      lastTime = timestamp;
      if (canvas.width !== canvas.clientWidth || canvas.height !== canvas.clientHeight) {
        canvas.width = canvas.clientWidth;
        canvas.height = canvas.clientHeight;
      }
      const cw = canvas.width;
      const ch = canvas.height;
      const state = gameStore.getState();
      const phase = state.phase;
      const isPlayable = phase === 'playing' && !state.isGameOver;
      const dialogueActive = state.dialogueQueue.length > 0;
      const shopOpen = state.shopOpen;
      const zone = ZONES[currentZoneId];
      // Hit-stop: reduce dt to near-zero briefly
      let gameDt = dt;
      if (hitStopTimer > 0) {
        gameDt = dt * 0.1;
        hitStopTimer -= dt;
      }
      const player = getPlayer();
      if (isPlayable && player && !shopOpen) {
        gameStore.setState({
          timeAlive: state.timeAlive + dt
        });
        if (state.viewership > 0) {
          gameStore.setState({
            viewership: Math.max(0, state.viewership - dt * 0.6)
          });
        }
        let moveDir = v2(0, 0);
        if (inputMap['w']) moveDir.y -= 1;
        if (inputMap['s']) moveDir.y += 1;
        if (inputMap['a']) moveDir.x -= 1;
        if (inputMap['d']) moveDir.x += 1;
        if (moveDir.x !== 0 || moveDir.y !== 0) {
          moveDir = v2Norm(moveDir);
          idleTime = 0;
          player.vel = v2Add(player.vel, v2Scale(moveDir, player.speed * gameDt * 8));
          player.facing = moveDir;
          player.walkPhase = (player.walkPhase || 0) + gameDt * 10;
          if (!gameStore.hasStoryFlag('has_moved')) gameStore.setStoryFlag('has_moved');
        } else {
          idleTime += dt;
          player.walkPhase = 0;
        }
        if (player.damageFlash && player.damageFlash > 0) player.damageFlash -= dt;
        const now = Date.now();
        if (inputMap[' ']) {
          if (now - lastKickTime > 500) {
            lastKickTime = now;
            performKick();
          }
          inputMap[' '] = false;
        }
        if (inputMap['e']) {
          if (now - lastInteractTime > 350) {
            lastInteractTime = now;
            const interacted = tryInteract();
            if (!interacted && now - lastStompTime > 800) {
              lastStompTime = now;
              performStomp();
            }
          }
          inputMap['e'] = false;
        }
        mordecaiTimer += dt;
        if (mordecaiTimer > 25 + Math.random() * 20) {
          mordecaiTimer = 0;
          if (!dialogueActive && Math.random() < 0.6) {
            gameStore.addMessage(mordecaiAmbient[Math.floor(Math.random() * mordecaiAmbient.length)], 'mordecai');
          }
        }
        if ((zone.kind === 'combat' || zone.kind === 'boss') && idleTime > 6 && Math.random() < 0.004) {
          gameStore.addMessage(getQuip('onIdle'), 'system');
          gameStore.setState({
            viewership: Math.max(0, state.viewership - 3)
          });
        }
        if (state.health < 25 && state.health > 0 && !lowHealthWarned) {
          lowHealthWarned = true;
          gameStore.addMessage(getQuip('onLowHealth'), 'system');
        }
        if (state.health > 30) lowHealthWarned = false;
        if (zone.kind === 'combat' || zone.kind === 'boss') {
          if (state.viewership >= 80) {
            highViewershipTime += dt;
            if (now - lastSponsorDrop > 15000 && highViewershipTime > 3) {
              lastSponsorDrop = now;
              const angle = Math.random() * Math.PI * 2;
              const dist = Math.random() * zone.size * 0.5;
              entities.push(makeEntity('sponsor', v2(zone.x + Math.cos(angle) * dist, zone.z + Math.sin(angle) * dist), {
                color: '#ffd700',
                size: 1.3,
                mass: 2,
                hp: 1,
                maxHp: 1,
                friction: 0.9
              }));
              gameStore.addMessage(getQuip('onSponsorDrop'), 'loot');
            }
          } else highViewershipTime = 0;
        }
        // Ambient dust motes periodically
        ambientMoteTimer += dt;
        if (ambientMoteTimer > 0.3) {
          ambientMoteTimer = 0;
          const px = zone.x + (Math.random() - 0.5) * zone.size * 1.6;
          const py = zone.z + (Math.random() - 0.5) * zone.size * 1.6;
          addParticle({
            pos: v2(px, py),
            vel: v2((Math.random() - 0.5) * 0.5, -0.3 - Math.random() * 0.3),
            life: 4 + Math.random() * 2,
            maxLife: 6,
            color: `rgba(${zone.accentColor}, 0.2)`,
            size: 2,
            kind: 'mote'
          });
        }
        if (!dialogueActive) {
          entities.forEach(e => {
            if (e.kind !== 'enemy' || e.dead) return;
            if (e.damageFlash && e.damageFlash > 0) e.damageFlash -= dt;
            if (e.hp <= 0) {
              killEnemy(e, false);
              return;
            }
            const spd = v2Len(e.vel);
            const distFromCenter = v2Dist(e.pos, v2(zone.x, zone.z));
            if (spd > 12 && distFromCenter > zone.size - 1) {
              killEnemy(e, true);
              return;
            }
            if (spd < 6) {
              const dir = v2Norm(v2Sub(player.pos, e.pos));
              e.vel = v2Add(e.vel, v2Scale(dir, e.speed * gameDt * 5));
            }
            if (v2Dist(e.pos, player.pos) < 1.5 && now - e.lastDamageToPlayer > 800) {
              e.lastDamageToPlayer = now;
              const dmg = e.isBrute ? 20 : e.isAssassin ? 15 : 10;
              damageInstances++;
              player.damageFlash = 0.15;
              camShake = Math.max(camShake, 5);
              const newHealth = state.health - dmg;
              gameStore.setState({
                health: newHealth
              });
              if (newHealth <= 0) {
                handlePlayerDeath();
                return;
              }
            }
          });
          entities.forEach(e => {
            if (e.kind !== 'boss' || e.dead) return;
            if (e.hp <= 0) {
              killBoss(e);
              return;
            }
            const spd = v2Len(e.vel);
            if (spd < 5) {
              const dir = v2Norm(v2Sub(player.pos, e.pos));
              e.vel = v2Add(e.vel, v2Scale(dir, e.speed * gameDt * 3));
            }
            if (v2Dist(e.pos, player.pos) < 3 && now - e.lastDamageToPlayer > 1200) {
              e.lastDamageToPlayer = now;
              damageInstances++;
              player.damageFlash = 0.2;
              camShake = Math.max(camShake, 10);
              const newHealth = state.health - 30;
              gameStore.setState({
                health: newHealth
              });
              gameStore.addMessage('The Goblin King connects.', 'roast');
              if (newHealth <= 0) {
                handlePlayerDeath();
                return;
              }
            }
          });
          // Creature wandering
          entities.forEach(e => {
            if (e.kind !== 'creature' || e.dead) return;
            e.wanderTimer = (e.wanderTimer || 0) - dt;
            if (e.wanderTimer! <= 0 || !e.wanderTarget || v2Dist(e.pos, e.wanderTarget) < 1) {
              e.wanderTimer = 2 + Math.random() * 3;
              e.wanderTarget = v2(zone.x + (Math.random() - 0.5) * zone.size * 1.6, zone.z + (Math.random() - 0.5) * zone.size * 1.6);
            }
            // Flee from player if too close
            const playerDist = v2Dist(player.pos, e.pos);
            let target = e.wanderTarget;
            if (playerDist < 4) {
              target = v2Add(e.pos, v2Scale(v2Norm(v2Sub(e.pos, player.pos)), 5));
            }
            const dir = v2Norm(v2Sub(target, e.pos));
            e.vel = v2Add(e.vel, v2Scale(dir, (e.speed || 4) * gameDt * 3));
          });
        }
        // Crate-enemy kills
        entities.forEach(crate => {
          if (crate.kind !== 'crate' && crate.kind !== 'sponsor' || crate.dead) return;
          if (v2Len(crate.vel) < 8) return;
          entities.forEach(enemy => {
            if (enemy.kind !== 'enemy' && enemy.kind !== 'boss' || enemy.dead) return;
            if (v2Dist(crate.pos, enemy.pos) < 2) {
              if (enemy.kind === 'boss') {
                enemy.hp -= 40;
                if (enemy.hp <= 0) killBoss(enemy);
              } else killEnemy(enemy, true);
              if (crate.kind === 'crate') {
                crate.dead = true;
                gameStore.setState({
                  cratesDestroyed: state.cratesDestroyed + 1
                });
              }
            }
          });
        });
        // Sponsor pickup
        entities.forEach(e => {
          if (e.kind !== 'sponsor' || e.dead) return;
          if (v2Dist(player.pos, e.pos) < 2 && v2Len(e.vel) < 3) {
            e.dead = true;
            const s = gameStore.getState();
            gameStore.setState({
              health: Math.min(s.maxHealth, s.health + 25),
              viewership: Math.min(100, s.viewership + 15),
              gold: s.gold + 5
            });
            gameStore.addMessage('Sponsor package consumed. Side effects may include: survival.', 'loot');
          }
        });
        // Pickup collection (gold/health)
        entities.forEach(e => {
          if (e.kind !== 'pickup' || e.dead) return;
          const d = v2Dist(player.pos, e.pos);
          // Magnet pickup when close
          if (d < 2) {
            const dir = v2Norm(v2Sub(player.pos, e.pos));
            e.vel = v2Add(e.vel, v2Scale(dir, 12 * gameDt));
          }
          if (d < 0.8) {
            e.dead = true;
            if (e.pickupType === 'gold') gameStore.setState({
              gold: state.gold + (e.pickupValue || 1)
            });else if (e.pickupType === 'health') gameStore.setState({
              health: Math.min(state.maxHealth, state.health + (e.pickupValue || 25))
            });
          }
        });
        // Portal walk-through (only after leaving portal area + cooldown)
        entities.forEach(e => {
          if (e.kind === 'portal' && !e.dead) {
            const d = v2Dist(player.pos, e.pos);
            if (d > 2.5) (e as any)._armed = true;
            if ((e as any)._armed && d < 1.0 && now - lastInteractTime > 2000) {
              lastInteractTime = now;
              const target = e.portalTarget;
              if (target) loadZone(target);
            }
          }
        });
      }
      // Particle physics
      particles = particles.filter(p => {
        p.life -= dt;
        if (p.life <= 0) return false;
        p.pos = v2Add(p.pos, v2Scale(p.vel, gameDt));
        p.vel = v2Scale(p.vel, 0.94);
        if (p.kind === 'coin') p.vel.y += 15 * gameDt; // gravity
        if (p.kind === 'ring') p.size += 60 * gameDt;
        return true;
      });
      // Entity physics
      entities.forEach(e => {
        if (e.dead) return;
        if (e.kind === 'pillar' || e.kind === 'npc' || e.kind === 'portal' || e.kind === 'stairs') return;
        e.pos = v2Add(e.pos, v2Scale(e.vel, gameDt));
        e.vel = v2Scale(e.vel, e.friction);
        const zc = v2(zone.x, zone.z);
        const dx = e.pos.x - zc.x;
        const dy = e.pos.y - zc.y;
        const maxDist = zone.size - 0.5;
        if (Math.abs(dx) > maxDist) {
          e.pos.x = zc.x + Math.sign(dx) * maxDist;
          e.vel.x = -e.vel.x * 0.3;
        }
        if (Math.abs(dy) > maxDist) {
          e.pos.y = zc.y + Math.sign(dy) * maxDist;
          e.vel.y = -e.vel.y * 0.3;
        }
      });
      // Separation
      for (let i = 0; i < entities.length; i++) {
        const a = entities[i];
        if (a.dead || a.kind === 'pillar' || a.kind === 'npc' || a.kind === 'portal' || a.kind === 'stairs' || a.kind === 'creature' || a.kind === 'pickup') continue;
        for (let j = i + 1; j < entities.length; j++) {
          const b = entities[j];
          if (b.dead || b.kind === 'pillar' || b.kind === 'npc' || b.kind === 'portal' || b.kind === 'stairs' || b.kind === 'creature' || b.kind === 'pickup') continue;
          if (a.kind === 'player' && (b.kind === 'enemy' || b.kind === 'boss')) continue;
          if ((a.kind === 'enemy' || a.kind === 'boss') && b.kind === 'player') continue;
          const dist = v2Dist(a.pos, b.pos);
          const minDist = (a.size + b.size) * 0.45;
          if (dist < minDist && dist > 0.01) {
            const dir = v2Norm(v2Sub(a.pos, b.pos));
            const overlap = minDist - dist;
            const totalMass = a.mass + b.mass;
            a.pos = v2Add(a.pos, v2Scale(dir, overlap * (b.mass / totalMass)));
            b.pos = v2Sub(b.pos, v2Scale(dir, overlap * (a.mass / totalMass)));
          }
        }
      }
      entities = entities.filter(e => !e.dead);
      if (flashTimer > 0) flashTimer -= dt;
      if (camShake > 0) camShake -= dt * 40;
      if (zoneEnterFlash > 0) zoneEnterFlash -= dt * 1.6;
      // Smooth scale lerp toward target
      scale += (scaleTarget - scale) * 0.08;
      // Camera follow + shake + lookahead
      const shakeX = camShake > 0 ? (Math.random() - 0.5) * camShake * 0.1 : 0;
      const shakeY = camShake > 0 ? (Math.random() - 0.5) * camShake * 0.1 : 0;
      if (player) {
        const lookahead = v2Scale(player.facing || v2(0, 0), 1.5);
        const target = v2Add(player.pos, lookahead);
        cam = v2Add(cam, v2Scale(v2Sub(target, cam), 0.08));
      }
      const camRender = v2(cam.x + shakeX, cam.y + shakeY);
      // ── RENDER ──
      // Dark void background
      ctx.fillStyle = '#050308';
      ctx.fillRect(0, 0, cw, ch);
      // Zone ambient tint
      const bgGrad = ctx.createRadialGradient(cw / 2, ch / 2, 0, cw / 2, ch / 2, Math.max(cw, ch) * 0.6);
      bgGrad.addColorStop(0, zone.lightColor);
      bgGrad.addColorStop(1, '#050308');
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, cw, ch);
      // ── TILED ISO FLOOR with torch light pools ──
      const zoneCenter = v2(zone.x, zone.z);
      const tileSize = 1.2;
      const tilesPerSide = Math.ceil(zone.size / tileSize);
      // Precompute torches for light pools
      const torches: {
        pos: Vec2;
        intensity: number;
      }[] = [];
      entities.forEach(e => {
        if (e.kind === 'prop' && e.propType === 'torch' && !e.dead) {
          const flicker = 0.85 + Math.sin(timestamp / 80 + (e.glowPhase || 0)) * 0.15;
          torches.push({
            pos: e.pos,
            intensity: flicker
          });
        }
      });
      // Hearth also emits light
      entities.forEach(e => {
        if (e.kind === 'prop' && e.propType === 'hearth' && !e.dead) {
          torches.push({
            pos: e.pos,
            intensity: 1.2
          });
        }
      });
      // Parse zone colors to RGB
      const parseHex = (h: string): [number, number, number] => {
        const v = parseInt(h.replace('#', ''), 16);
        return [v >> 16 & 255, v >> 8 & 255, v & 255];
      };
      const [fr, fg, fb] = parseHex(zone.floorColor);
      const [ar, ag, ab] = parseHex(zone.accentColor);
      // Zone-specific tile decoration
      const zoneTheme = zone.id;
      // Draw tiles (back-to-front for correct iso)
      for (let ty = -tilesPerSide; ty <= tilesPerSide; ty++) {
        for (let tx = -tilesPerSide; tx <= tilesPerSide; tx++) {
          const wx = zone.x + tx * tileSize;
          const wz = zone.z + ty * tileSize;
          // Stable seeded variance per tile
          const seed = tx * 374761393 + ty * 668265263 & 0x7fffffff;
          const variance = (seed % 100 - 50) / 600; // ±8%
          let r = fr * (1 + variance);
          let g = fg * (1 + variance);
          let b = fb * (1 + variance);
          // Torch light contribution
          let lightR = 0,
            lightG = 0,
            lightB = 0;
          for (const t of torches) {
            const d = v2Dist(v2(wx, wz), t.pos);
            if (d < 7) {
              const falloff = Math.pow(1 - d / 7, 2) * t.intensity;
              lightR += 255 * falloff * 0.5;
              lightG += 170 * falloff * 0.5;
              lightB += 80 * falloff * 0.5;
            }
          }
          r = Math.min(255, r + lightR);
          g = Math.min(255, g + lightG);
          b = Math.min(255, b + lightB);
          // Edge darkening (radial toward walls)
          const edgeDist = Math.max(Math.abs(tx), Math.abs(ty)) / tilesPerSide;
          const edgeFade = Math.max(0, 1 - edgeDist * 0.4);
          r *= 0.7 + 0.3 * edgeFade;
          g *= 0.7 + 0.3 * edgeFade;
          b *= 0.7 + 0.3 * edgeFade;
          // Zone pattern accent lines
          const patternSeed = (tx + ty * 7) % 11;
          if (zoneTheme === 'throne_of_bones' && patternSeed === 0) {
            r = Math.min(255, r + 30);
            g *= 0.7;
            b *= 0.7;
          } else if (zoneTheme === 'donut_discovery' && (tx + ty) % 2 === 0) {
            r = Math.min(255, r + 15);
            g = Math.min(255, g + 8);
            b = Math.min(255, b + 5);
          } else if (zoneTheme === 'merchant_nook') {
            // wood plank darken every third row
            if (ty % 3 === 0) {
              r *= 0.85;
              g *= 0.85;
              b *= 0.85;
            }
          } else if (zoneTheme === 'goblin_warrens' && patternSeed === 3) {
            r *= 0.8;
            g *= 0.85;
            b *= 0.8;
          }
          const sp = toScreen(v2(wx, wz), camRender, scale, cw, ch);
          const tw = tileSize * scale * Math.cos(ISO_ANGLE);
          const th = tileSize * scale * Math.sin(ISO_ANGLE) * 0.6;
          ctx.fillStyle = `rgb(${r | 0},${g | 0},${b | 0})`;
          ctx.strokeStyle = `rgba(0,0,0,0.35)`;
          ctx.lineWidth = 0.8;
          ctx.beginPath();
          ctx.moveTo(sp.x, sp.y - th);
          ctx.lineTo(sp.x + tw, sp.y);
          ctx.lineTo(sp.x, sp.y + th);
          ctx.lineTo(sp.x - tw, sp.y);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          // Rare floor crack/decal
          if (seed % 97 < 4) {
            ctx.strokeStyle = `rgba(0,0,0,0.5)`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(sp.x - tw * 0.5, sp.y - th * 0.2);
            ctx.lineTo(sp.x + tw * 0.3, sp.y + th * 0.3);
            ctx.stroke();
          }
          // Zone-specific scatter decals
          if (zoneTheme === 'tutorial_hollow' && seed % 89 < 3) {
            ctx.fillStyle = `rgba(60,10,15,0.35)`;
            ctx.beginPath();
            ctx.ellipse(sp.x, sp.y, tw * 0.35, th * 0.6, 0, 0, Math.PI * 2);
            ctx.fill();
          }
          if (zoneTheme === 'goblin_warrens' && seed % 73 < 4) {
            ctx.fillStyle = `rgba(220,200,170,0.4)`;
            ctx.beginPath();
            ctx.arc(sp.x + tw * 0.2, sp.y, 1.5, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }
      // ── WALLS with portal gaps ──
      const wallHeight = 2.4;
      const portalPositions: Vec2[] = entities.filter(e => e.kind === 'portal' && !e.dead).map(e => e.pos);
      const isNearPortal = (wx: number, wz: number) => {
        for (const pp of portalPositions) {
          if (v2Dist(v2(wx, wz), pp) < 2.2) return true;
        }
        return false;
      };
      const drawWallSegment = (p1: Vec2, p2: Vec2, which: 'NW' | 'NE' | 'SE' | 'SW') => {
        const steps = Math.ceil(v2Dist(p1, p2) / 0.8);
        for (let i = 0; i < steps; i++) {
          const t1 = i / steps;
          const t2 = (i + 1) / steps;
          const wx1 = p1.x + (p2.x - p1.x) * t1;
          const wz1 = p1.y + (p2.y - p1.y) * t1;
          const wx2 = p1.x + (p2.x - p1.x) * t2;
          const wz2 = p1.y + (p2.y - p1.y) * t2;
          const mx = (wx1 + wx2) / 2;
          const mz = (wz1 + wz2) / 2;
          if (isNearPortal(mx, mz)) continue;
          const b1 = toScreen(v2(wx1, wz1), camRender, scale, cw, ch);
          const b2 = toScreen(v2(wx2, wz2), camRender, scale, cw, ch);
          const topH = wallHeight * scale * 0.5;
          // Side face (darker)
          const isBack = which === 'NW' || which === 'NE';
          ctx.fillStyle = isBack ? `rgba(${ar * 0.18 | 0},${ag * 0.18 | 0},${ab * 0.18 | 0},1)` : `rgba(${ar * 0.08 | 0},${ag * 0.08 | 0},${ab * 0.08 | 0},1)`;
          ctx.strokeStyle = 'rgba(0,0,0,0.7)';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(b1.x, b1.y);
          ctx.lineTo(b2.x, b2.y);
          ctx.lineTo(b2.x, b2.y - topH);
          ctx.lineTo(b1.x, b1.y - topH);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          // Top rim (lighter accent)
          ctx.fillStyle = `rgba(${Math.min(255, ar * 0.55) | 0},${Math.min(255, ag * 0.55) | 0},${Math.min(255, ab * 0.55) | 0},0.9)`;
          ctx.beginPath();
          ctx.moveTo(b1.x, b1.y - topH);
          ctx.lineTo(b2.x, b2.y - topH);
          ctx.lineTo(b2.x, b2.y - topH - 3);
          ctx.lineTo(b1.x, b1.y - topH - 3);
          ctx.closePath();
          ctx.fill();
          // Brick line
          if (i % 2 === 0) {
            ctx.strokeStyle = 'rgba(0,0,0,0.5)';
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            const midY = (b1.y + b2.y) / 2 - topH * 0.5;
            ctx.moveTo(b1.x, midY);
            ctx.lineTo(b2.x, midY);
            ctx.stroke();
          }
        }
      };
      const wc = {
        NW: v2(zone.x - zone.size, zone.z - zone.size),
        NE: v2(zone.x + zone.size, zone.z - zone.size),
        SE: v2(zone.x + zone.size, zone.z + zone.size),
        SW: v2(zone.x - zone.size, zone.z + zone.size)
      };
      // Draw back walls first (so they render behind entities)
      drawWallSegment(wc.NW, wc.NE, 'NW');
      drawWallSegment(wc.NE, wc.SE, 'NE');
      // Portal arches (glowing gap)
      portalPositions.forEach(pp => {
        const sp = toScreen(pp, camRender, scale, cw, ch);
        const archGrad = ctx.createRadialGradient(sp.x, sp.y - 30, 0, sp.x, sp.y - 30, 80);
        archGrad.addColorStop(0, 'rgba(168,85,247,0.35)');
        archGrad.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = archGrad;
        ctx.fillRect(sp.x - 80, sp.y - 110, 160, 160);
      });
      // Player pulsing halo (drawn under the player sprite)
      if (player) {
        const pp = toScreen(player.pos, camRender, scale, cw, ch);
        const pulse = 0.8 + Math.sin(timestamp / 400) * 0.2;
        const haloR = scale * 1.1 * pulse;
        const haloGrad = ctx.createRadialGradient(pp.x, pp.y + 4, 0, pp.x, pp.y + 4, haloR);
        haloGrad.addColorStop(0, `rgba(255,220,120,${0.35 * pulse})`);
        haloGrad.addColorStop(0.5, `rgba(255,180,80,${0.15 * pulse})`);
        haloGrad.addColorStop(1, 'rgba(255,180,80,0)');
        ctx.fillStyle = haloGrad;
        ctx.fillRect(pp.x - haloR, pp.y + 4 - haloR, haloR * 2, haloR * 2);
        // Breathing ring
        ctx.strokeStyle = `rgba(255,220,120,${0.4 * pulse})`;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.ellipse(pp.x, pp.y + 4, scale * 0.7 * pulse, scale * 0.28 * pulse, 0, 0, Math.PI * 2);
        ctx.stroke();
        // Facing footprint (kick direction indicator)
        const f = player.facing || v2(0, 1);
        if (Math.abs(f.x) > 0.05 || Math.abs(f.y) > 0.05) {
          const footDist = 2;
          const footPos = v2(player.pos.x + f.x * footDist, player.pos.y + f.y * footDist);
          const fp = toScreen(footPos, camRender, scale, cw, ch);
          ctx.strokeStyle = `rgba(255,220,120,${0.55 + Math.sin(timestamp / 250) * 0.2})`;
          ctx.fillStyle = 'rgba(255,220,120,0.15)';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.ellipse(fp.x, fp.y, scale * 0.35, scale * 0.14, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
          // Arrow tip
          const ang = Math.atan2(fp.y - pp.y, fp.x - pp.x);
          ctx.beginPath();
          ctx.moveTo(fp.x + Math.cos(ang) * scale * 0.35, fp.y + Math.sin(ang) * scale * 0.14);
          ctx.lineTo(fp.x + Math.cos(ang) * scale * 0.55, fp.y + Math.sin(ang) * scale * 0.22);
          ctx.stroke();
        }
      }
      // Sort entities by y
      const sorted = [...entities].sort((a, b) => a.pos.y - b.pos.y);
      sorted.forEach(e => {
        if (!e.dead) drawEntity(ctx, e, camRender, scale, cw, ch, timestamp);
      });
      // Front walls (drawn after entities so they occlude props near the wall)
      drawWallSegment(wc.SE, wc.SW, 'SE');
      drawWallSegment(wc.SW, wc.NW, 'SW');
      // Render particles (on top)
      particles.forEach(p => {
        const sp = toScreen(p.pos, camRender, scale, cw, ch);
        const alpha = p.life / p.maxLife;
        if (p.kind === 'ring') {
          ctx.strokeStyle = p.color.replace(/[\d.]+\)$/, `${alpha})`);
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.arc(sp.x, sp.y, p.size, 0, Math.PI * 2);
          ctx.stroke();
        } else if (p.kind === 'coin') {
          ctx.shadowColor = '#ffd700';
          ctx.shadowBlur = 6;
          ctx.fillStyle = `rgba(255,215,0,${alpha})`;
          ctx.beginPath();
          ctx.arc(sp.x, sp.y, p.size, 0, Math.PI * 2);
          ctx.fill();
          ctx.shadowBlur = 0;
        } else if (p.kind === 'mote') {
          ctx.fillStyle = `rgba(255,255,255,${alpha * 0.3})`;
          ctx.fillRect(sp.x - 1, sp.y - 1, 2, 2);
        } else {
          ctx.fillStyle = p.color;
          ctx.globalAlpha = alpha;
          ctx.beginPath();
          ctx.arc(sp.x, sp.y, p.size, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1;
        }
      });
      // Kick arc flash on kick
      if (player && kickArcTimer > 0) {
        kickArcTimer -= dt;
        const pp = toScreen(player.pos, camRender, scale, cw, ch);
        const alpha = Math.max(0, kickArcTimer / 0.25);
        // Wedge in facing direction
        const f = kickArcDir;
        const fAng = Math.atan2(f.x + f.y, f.x - f.y) + Math.PI / 4;
        ctx.save();
        ctx.translate(pp.x, pp.y);
        ctx.rotate(fAng);
        const arcR = 5 * scale * 0.55;
        const wedge = Math.PI * 0.55;
        const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, arcR);
        grad.addColorStop(0, `rgba(255,240,160,${0.7 * alpha})`);
        grad.addColorStop(0.6, `rgba(255,180,80,${0.35 * alpha})`);
        grad.addColorStop(1, 'rgba(255,180,80,0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.arc(0, 0, arcR, -wedge / 2, wedge / 2);
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = `rgba(255,240,160,${alpha})`;
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.restore();
      }
      // Interact prompt
      if (player && isPlayable && !dialogueActive && !shopOpen) {
        let nearest: Entity | null = null;
        let nearestDist = 3;
        entities.forEach(e => {
          if (e.dead) return;
          if (e.kind === 'npc' || e.kind === 'portal' || e.kind === 'stairs') {
            const d = v2Dist(player.pos, e.pos);
            if (d < nearestDist) {
              nearest = e;
              nearestDist = d;
            }
          }
        });
        if (nearest) {
          const n = nearest as Entity;
          const ss = toScreen(n.pos, camRender, scale, cw, ch);
          ctx.font = 'bold 12px "Share Tech Mono", monospace';
          ctx.fillStyle = '#ffffff';
          ctx.strokeStyle = 'rgba(0,0,0,0.9)';
          ctx.lineWidth = 3;
          ctx.textAlign = 'center';
          const label = n.kind === 'portal' ? '[E] or walk in' : n.kind === 'stairs' ? '▼ [E] DESCEND' : n.npcSpeaker === 'gnome' ? '[E] SHOP' : '[E] TALK';
          ctx.strokeText(label, ss.x, ss.y + scale * 2);
          ctx.fillText(label, ss.x, ss.y + scale * 2);
        }
      }
      // Vignette
      const grad = ctx.createRadialGradient(cw / 2, ch / 2, 0, cw / 2, ch / 2, Math.max(cw, ch) * 0.7);
      grad.addColorStop(0, 'rgba(0,0,0,0)');
      grad.addColorStop(1, 'rgba(0,0,0,0.6)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, cw, ch);
      // Damage flash full-screen
      if (player && player.damageFlash && player.damageFlash > 0) {
        ctx.fillStyle = `rgba(220,40,40,${player.damageFlash * 0.3})`;
        ctx.fillRect(0, 0, cw, ch);
      }
      // Zone-enter flash
      if (zoneEnterFlash > 0) {
        const [er, eg, eb] = parseHex(zoneEnterColor);
        ctx.fillStyle = `rgba(${er},${eg},${eb},${zoneEnterFlash * 0.25})`;
        ctx.fillRect(0, 0, cw, ch);
      }
      // ── OBJECTIVE ARROW ──
      if (player && isPlayable && !dialogueActive && !shopOpen) {
        const remainingEnemies = entities.filter(e => (e.kind === 'enemy' || e.kind === 'boss') && !e.dead).length;
        // Show arrow when zone is cleared or narrative zone
        if (remainingEnemies === 0) {
          // Find nearest unvisited portal, else any portal
          const portals = entities.filter(e => e.kind === 'portal' && !e.dead);
          const visited = gameStore.getState().visitedZones;
          let best: Entity | null = null;
          let bestScore = Infinity;
          portals.forEach(p => {
            const t = p.portalTarget;
            const unvisitedBonus = t && !visited.includes(t) ? 0 : 50;
            const d = v2Dist(player.pos, p.pos) + unvisitedBonus;
            if (d < bestScore) {
              bestScore = d;
              best = p;
            }
          });
          const stairs = entities.find(e => e.kind === 'stairs' && !e.dead);
          const target = stairs as Entity | undefined || best as Entity | null;
          if (target && v2Dist(player.pos, target.pos) > 3) {
            const dir = v2Norm(v2Sub(target.pos, player.pos));
            const arrowPos = v2(player.pos.x + dir.x * 2.5, player.pos.y + dir.y * 2.5);
            const ap = toScreen(arrowPos, camRender, scale, cw, ch);
            const bob = Math.sin(timestamp / 300) * 4;
            const ang = Math.atan2(dir.x + dir.y, dir.x - dir.y) + Math.PI / 4;
            ctx.save();
            ctx.translate(ap.x, ap.y + bob);
            ctx.rotate(ang);
            ctx.fillStyle = `rgba(255,220,120,${0.5 + Math.sin(timestamp / 300) * 0.2})`;
            ctx.strokeStyle = 'rgba(0,0,0,0.6)';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(20, 0);
            ctx.lineTo(-10, -12);
            ctx.lineTo(-5, 0);
            ctx.lineTo(-10, 12);
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
            ctx.restore();
          }
        } else {
          // First-enemy onboarding: mark nearest enemy in tutorial_hollow before first kick
          if (currentZoneId === 'tutorial_hollow' && !gameStore.hasStoryFlag('has_kicked')) {
            let nearestE: Entity | null = null;
            let nearestED = Infinity;
            entities.forEach(e => {
              if (e.kind === 'enemy' && !e.dead) {
                const d = v2Dist(player.pos, e.pos);
                if (d < nearestED) {
                  nearestED = d;
                  nearestE = e;
                }
              }
            });
            if (nearestE) {
              const ne = nearestE as Entity;
              const ep = toScreen(ne.pos, camRender, scale, cw, ch);
              const pulse = 0.7 + Math.sin(timestamp / 200) * 0.3;
              ctx.strokeStyle = `rgba(255,80,80,${pulse})`;
              ctx.lineWidth = 2;
              ctx.beginPath();
              ctx.ellipse(ep.x, ep.y + 8, scale * 0.8 * pulse, scale * 0.32 * pulse, 0, 0, Math.PI * 2);
              ctx.stroke();
              ctx.font = 'bold 12px "Share Tech Mono", monospace';
              ctx.fillStyle = '#ff6060';
              ctx.strokeStyle = 'rgba(0,0,0,0.9)';
              ctx.lineWidth = 3;
              ctx.textAlign = 'center';
              ctx.strokeText('◄ KICK ME  [SPACE]', ep.x, ep.y - scale * 1.3);
              ctx.fillText('◄ KICK ME  [SPACE]', ep.x, ep.y - scale * 1.3);
            }
          }
        }
      }
      // ── MINIMAP (top-right) ──
      {
        const mmW = 180;
        const mmH = 130;
        const mmX = cw - mmW - 16;
        const mmY = 100;
        ctx.fillStyle = 'rgba(5,3,8,0.85)';
        ctx.fillRect(mmX, mmY, mmW, mmH);
        ctx.strokeStyle = 'rgba(168,85,247,0.5)';
        ctx.lineWidth = 1;
        ctx.strokeRect(mmX, mmY, mmW, mmH);
        // World bounds for minimap projection
        const zoneList = Object.values(ZONES).filter(z => z.id !== 'stairs_down');
        let minX = Infinity,
          maxX = -Infinity,
          minZ = Infinity,
          maxZ = -Infinity;
        zoneList.forEach(z => {
          minX = Math.min(minX, z.x - z.size);
          maxX = Math.max(maxX, z.x + z.size);
          minZ = Math.min(minZ, z.z - z.size);
          maxZ = Math.max(maxZ, z.z + z.size);
        });
        const pad = 12;
        const sxScale = (mmW - pad * 2) / (maxX - minX);
        const syScale = (mmH - pad * 2) / (maxZ - minZ);
        const mm = (wx: number, wz: number) => ({
          x: mmX + pad + (wx - minX) * sxScale,
          y: mmY + pad + (wz - minZ) * syScale
        });
        const visited = gameStore.getState().visitedZones;
        // Connections
        ctx.strokeStyle = 'rgba(168,85,247,0.3)';
        ctx.lineWidth = 1;
        zoneList.forEach(z => {
          z.exits.forEach(ex => {
            const to = ZONES[ex.to];
            if (!to) return;
            const a = mm(z.x, z.z);
            const b = mm(to.x, to.z);
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          });
        });
        // Zone rectangles
        zoneList.forEach(z => {
          const p = mm(z.x, z.z);
          const w = z.size * sxScale * 1.2;
          const h = z.size * syScale * 1.2;
          const isCurrent = z.id === currentZoneId;
          const isVisited = visited.includes(z.id);
          if (isCurrent) {
            const pulse = 0.6 + Math.sin(timestamp / 300) * 0.3;
            ctx.fillStyle = z.accentColor;
            ctx.globalAlpha = pulse;
          } else if (isVisited) {
            ctx.fillStyle = z.accentColor;
            ctx.globalAlpha = 0.4;
          } else {
            ctx.fillStyle = '#2a2030';
            ctx.globalAlpha = 0.6;
          }
          ctx.fillRect(p.x - w / 2, p.y - h / 2, w, h);
          ctx.globalAlpha = 1;
          ctx.strokeStyle = isCurrent ? '#ffffff' : 'rgba(255,255,255,0.2)';
          ctx.lineWidth = isCurrent ? 1.5 : 0.5;
          ctx.strokeRect(p.x - w / 2, p.y - h / 2, w, h);
        });
        // Player dot
        if (player) {
          const pd = mm(player.pos.x, player.pos.y);
          ctx.shadowColor = '#ffd700';
          ctx.shadowBlur = 6;
          ctx.fillStyle = '#ffd700';
          ctx.beginPath();
          ctx.arc(pd.x, pd.y, 3, 0, Math.PI * 2);
          ctx.fill();
          ctx.shadowBlur = 0;
        }
        // Current zone label
        ctx.font = 'bold 10px "Share Tech Mono", monospace';
        ctx.fillStyle = zone.accentColor;
        ctx.textAlign = 'left';
        ctx.fillText(zone.name.toUpperCase(), mmX + 6, mmY + mmH + 14);
      }
      // ── CONTROL LEGEND (bottom-left) ──
      {
        const legX = 16;
        const legY = ch - 44;
        const flags = gameStore.getState().storyFlags;
        const hints = [{
          key: 'WASD',
          label: 'MOVE',
          used: !!flags['has_moved'],
          next: !flags['has_moved']
        }, {
          key: 'SPACE',
          label: 'KICK',
          used: !!flags['has_kicked'],
          next: !!flags['has_moved'] && !flags['has_kicked']
        }, {
          key: 'E',
          label: 'INTERACT',
          used: !!flags['has_interacted'],
          next: !!flags['has_kicked'] && !flags['has_interacted']
        }];
        ctx.font = 'bold 11px "Share Tech Mono", monospace';
        ctx.textAlign = 'left';
        ctx.fillStyle = 'rgba(5,3,8,0.75)';
        ctx.fillRect(legX - 6, legY - 6, 340, 30);
        ctx.strokeStyle = 'rgba(168,85,247,0.3)';
        ctx.strokeRect(legX - 6, legY - 6, 340, 30);
        let cx = legX;
        hints.forEach(h => {
          const pulse = h.next ? 0.75 + Math.sin(timestamp / 250) * 0.25 : 1;
          const alpha = h.used ? 0.4 : 1 * pulse;
          ctx.fillStyle = h.next ? `rgba(255,220,120,${alpha})` : `rgba(168,85,247,${alpha})`;
          ctx.fillText(`[${h.key}]`, cx, legY + 10);
          const keyW = ctx.measureText(`[${h.key}]`).width;
          ctx.fillStyle = `rgba(230,230,240,${alpha})`;
          ctx.fillText(h.label, cx + keyW + 6, legY + 10);
          cx += keyW + ctx.measureText(h.label).width + 24;
        });
      }
    }
    animId = requestAnimationFrame(gameLoop);
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('reset-game', resetHandler);
      unsub();
    };
  }, []);
  return <canvas ref={canvasRef} tabIndex={0} className="w-full h-full outline-none block" style={{
    touchAction: 'none'
  }} data-id="element-193" />;
}